"use client";

import { useEffect, useState, useRef, useMemo } from "react";
import {
  Building,
  Save,
  Plus,
  X,
  Search,
  Filter,
  Edit,
  Trash2,
  Sparkles,
  Info,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ArrowDownLeft,
  ArrowUpRight,
  Handshake,
  Unlink,
} from "lucide-react";
import Modal from "../layout/Modal";
import "./OrganizationInfoPage.css";
import BackButton from "../shared/backButton";
import {
  getCountriesListApi,
  getIndustriesListApi,
  getOrgInfoApi,
  saveOrgInfoApi,
  getDepartmentsListApi,
  deleteDepartmentApi,
  getDepartmentsMasterListApi,
  addDepartmentApi,
  generateCompanyInfoStream,
} from "../../services/settingsService";
import {
  handleApiError as baseHandleApiError,
  formatEmployeeCreatedAt,
} from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import {
  getPartnersListApi,
  getAdminDetailsByOrgCodeapi,
  editPartnerApi,
  addPartnerApi,
  getPartnerByIdApi,
  updatePartnerRequestApi,
  resendInvitationApi,
  removePartnerConnectionApi,
  cancelInvitationApi,
} from "../../services/hiringService";
import CustomTooltip from "../shared/CustomTooltip";
import Pagination from "../shared/paginator/paginator";
import BulkAction from "../shared/utils/bulkAction";
import { useTranslation } from "react-i18next";
import MessageManager from "../layout/Message";
import { useNavigate } from "react-router-dom";
import appConstants from "../shared/config/constants.json";
import {
  getOrganizationInfoAccess,
  getLoggedInUserRole,
  ROLES,
  getPartnersManagementAccess,
} from "../shared/access";
import {
  readPartnerCollaborationState,
  subscribePartnerCollaborationState,
  writePartnerCollaborationState,
} from "../shared/utils/partnerCollaborationState";
import {
  readPartnerInviteUnread,
  setPartnerInviteUnread,
  subscribePartnerInviteUnread,
  syncPendingPartnerInviteUnread,
} from "../shared/utils/partnerInviteNotificationState";

const OTHER_INDUSTRY_OPTION = "Other";

const isOtherIndustryOption = (value = "") =>
  value.trim().toLowerCase() === OTHER_INDUSTRY_OPTION.toLowerCase();
const normalizeBoolean = (value) =>
  value === true || value === "true" || value === 1 || value === "1";
const OrganizationInfoPage = () => {
  const { t } = useTranslation();
  const { MAYAMAYA_DEV_SUPPORT_URL } = appConstants;
  const [activeTab, setActiveTab] = useState("info");
  const partnerAccess = getPartnersManagementAccess();

  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    action: null,
  });
  // Departments state
  const [departments, setDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentSuggestions, setDepartmentSuggestions] = useState([]);

  const profileAllOption = "All";

  const levels = [
    {
      value: profileAllOption,
      label: t("common.all"),
    },
    {
      value: "Junior",
      label: t("employees.levels.junior"),
    },
    {
      value: "Mid Level",
      label: t("employees.levels.midLevel"),
    },
    {
      value: "Senior",
      label: t("employees.levels.senior"),
    },
    {
      value: "Lead",
      label: t("employees.levels.lead"),
    },
  ];

  const availabilityOptions = [
    {
      value: profileAllOption,
      label: t("common.all"),
    },
    {
      value: "Available",
      label: t("employees.availabilityOptions.available"),
    },
    {
      value: "Not Available",
      label: t("employees.availabilityOptions.notAvailable"),
    },
  ];
  const roleLevels = [
    {
      value: "All",
      label: t("common.all"),
    },
    {
      value: "Executive or Senior Management",
      label: t("jobRoles.levelOptions.executiveOrSeniorManagement"),
    },
    {
      value: "Middle Management",
      label: t("jobRoles.levelOptions.middleManagement"),
    },
    {
      value: "First-Level Management",
      label: t("jobRoles.levelOptions.firstLevelManagement"),
    },
    {
      value: "Intermediate or Experienced (Senior Staff)",
      label: t("jobRoles.levelOptions.intermediateOrExperiencedSeniorStaff"),
    },
    {
      value: "Entry-Level",
      label: t("jobRoles.levelOptions.entryLevel"),
    },
  ];

  const profileDepartmentOptions = [
    {
      value: profileAllOption,
      label: t("common.all"),
    },
    ...Array.from(
      new Set(
        (departments || [])
          .map((department) =>
            typeof department === "string" ? department : department?.name,
          )
          .filter(Boolean),
      ),
    ).map((departmentName) => ({
      value: departmentName,
      label: departmentName,
    })),
  ];

  const formatPartnerDate = (dateValue) => {
    if (!dateValue) return "-";
    return new Date(dateValue).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInviteExpiryDate = (dateValue) => {
    if (!dateValue) return "-";
    const expiryDate = new Date(dateValue);
    expiryDate.setDate(expiryDate.getDate() + 14);
    return expiryDate.toISOString().slice(0, 10);
  };

  const getPartnerConnectionSinceDate = (partner) => partner?.createdDate;
  const getInactiveReasonLabel = (partner) => {
    const reasonText = [
      partner?.inactiveReasonType,
      partner?.reasonType,
      partner?.statusReason,
      partner?.inactiveReason,
      partner?.invitation,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (reasonText.includes("expir")) {
      return "Invitation Expired";
    }

    if (reasonText.includes("declin") || reasonText.includes("reject")) {
      return "Invitation Declined";
    }

    if (reasonText.includes("cancel") || reasonText.includes("withdraw")) {
      return "Invitation Cancelled";
    }

    if (reasonText.includes("remove") || reasonText.includes("disconnect")) {
      return "Connection Removed";
    }

    return "Connection Removed";
  };

  const getInactiveStatusChangedDate = (partner) =>
    partner?.inactiveStatusChangedAt ||
    partner?.statusChangedAt ||
    partner?.inactiveAt ||
    partner?.disconnectedAt ||
    partner?.updatedDate ||
    partner?.updatedAt ||
    partner?.lastUpdated ||
    "";

  const getInactiveResponsibleOrganization = (partner) =>
    partner?.inactiveResponsibleOrganization ||
    partner?.responsibleOrganization ||
    partner?.actionOrganization ||
    partner?.updatedByOrganization ||
    "";

  const getInactiveResponsibleUser = (partner) =>
    partner?.inactiveResponsibleUser ||
    partner?.responsibleUser ||
    partner?.actionUser ||
    partner?.updatedByName ||
    partner?.updatedBy ||
    "";

  const getInactiveDescription = (partner) => {
    const description =
      partner?.inactiveDescription ||
      partner?.declineReason ||
      partner?.cancellationReason ||
      partner?.removalDescription ||
      partner?.removedReason ||
      partner?.inactiveReason ||
      "";

    const reasonLabel = getInactiveReasonLabel(partner);

    if (
      String(description).trim().toLowerCase() === reasonLabel.toLowerCase()
    ) {
      return "";
    }

    return description;
  };

  const [partners, setPartners] = useState([]);

  const [partnerCollaboration, setPartnerCollaboration] = useState(
    readPartnerCollaborationState,
  );

  useEffect(() => {
    return subscribePartnerCollaborationState(setPartnerCollaboration);
  }, []);

  /*
   * Do not derive the organization-level collaboration state from `partners`.
   * The `partners` array contains only the current filtered and paginated API
   * response. Changing a directory filter can therefore temporarily remove all
   * active partners from this array even though Partner Collaboration remains
   * enabled for the organization.
   *
   * `enabled` is synchronized only from getOrgInfoApi and the explicit
   * enable/disable actions below.
   */
  const [isUpdatingPartnerCollaboration, setIsUpdatingPartnerCollaboration] =
    useState(false);

  const [
    showDisablePartnerCollaborationModal,
    setShowDisablePartnerCollaborationModal,
  ] = useState(false);
  const handleEnablePartnerCollaboration = async () => {
    if (isUpdatingPartnerCollaboration) return;

    setIsUpdatingPartnerCollaboration(true);

    try {
      const obj = {
        orgName: orgInfos.org_name,
        description: orgInfos.description,
        industry: orgInfos.industry,
        website: orgInfos.website,
        country: orgInfos.country,
        upskillCourseType: upskillCourseType,
        partnerCollaboration: true,
      };

      const response = await saveOrgInfoApi(obj);

      if (response?.code === 200) {
        setOrgInfos((previous) => ({
          ...previous,
          partnerCollaboration: true,
        }));

        const updatedCollaborationState = writePartnerCollaborationState({
          enabled: true,
        });

        setPartnerCollaboration(updatedCollaborationState);

        MessageManager.showToast(
          "success",
          "Partner Collaboration enabled",
        );

        /*
         * Re-fetch to ensure the frontend has the value
         * actually saved by the backend.
         */
        await getOrgInfoHandler();
      }
    } catch (error) {
      console.error("Error enabling Partner Collaboration:", error);

      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsUpdatingPartnerCollaboration(false);
    }
  };

  const handleDisablePartnerCollaboration = async () => {
    if (isUpdatingPartnerCollaboration) return;

    setIsUpdatingPartnerCollaboration(true);

    try {
      const obj = {
        orgName: orgInfos.org_name,
        description: orgInfos.description,
        industry: orgInfos.industry,
        website: orgInfos.website,
        country: orgInfos.country,
        upskillCourseType,
        partnerCollaboration: false,
      };

      const response = await saveOrgInfoApi(obj);

      if (response?.code === 200) {
        setOrgInfos((previous) => ({
          ...previous,
          partnerCollaboration: false,
        }));

        const updatedCollaborationState = writePartnerCollaborationState({
          enabled: false,
          profileSharingEnabled: false,
          jobRoleSharingEnabled: false,
        });

        setPartnerCollaboration(updatedCollaborationState);

        setShowDisablePartnerCollaborationModal(false);
        setSelectedPartnerIds([]);
        setPartners([]);
        setPartnerTotalCount(0);
        setPartnerInviteUnread(false);

        // Reset directory controls so stale search/filter values do not hide
        // partner data the next time collaboration is enabled.
        setPartnerDirectorySearchInput("");
        setPartnerDirectorySearch("");
        setPartnerStatusFilter("All");
        setPartnerDirectionFilter("All");
        setPartnerSharedDataFilter("All");
        setPartnerCurrentPage(1);
        setShowPartnerDirectorySearch(false);
        setShowPartnerDirectoryFilters(false);

        MessageManager.showToast(
          "success",
          "Partner Collaboration has been disabled.",
        );

        await getOrgInfoHandler();
      }
    } catch (error) {
      console.error("Error disabling Partner Collaboration:", error);

      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setIsUpdatingPartnerCollaboration(false);
    }
  };

  const [partnerDirectorySearchInput, setPartnerDirectorySearchInput] =
    useState("");
  const [partnerDirectorySearch, setPartnerDirectorySearch] = useState("");
  const [showPartnerDirectorySearch, setShowPartnerDirectorySearch] =
    useState(false);
  const [showPartnerDirectoryFilters, setShowPartnerDirectoryFilters] =
    useState(false);
  const [partnerStatusFilter, setPartnerStatusFilter] = useState("All");
  const [partnerDirectionFilter, setPartnerDirectionFilter] = useState("All");
  const [partnerSharedDataFilter, setPartnerSharedDataFilter] = useState("All");
  const [partnerSort, setPartnerSort] = useState({
    field: "lastUpdated",
    direction: "desc",
  });
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [partnerCurrentPage, setPartnerCurrentPage] = useState(1);
  const [partnerItemsPerPage, setPartnerItemsPerPage] = useState(10);
  const [partnerTotalCount, setPartnerTotalCount] = useState(0);
  const [isPartnersLoading, setIsPartnersLoading] = useState(false);
  const [showPartnerBulkDeleteModal, setShowPartnerBulkDeleteModal] =
    useState(false);
  const [singlePartnerIdToRemove, setSinglePartnerIdToRemove] = useState(null);
  const [hasUnreadPartnerInvite, setHasUnreadPartnerInvite] = useState(
    readPartnerInviteUnread,
  );

  useEffect(() => {
    return subscribePartnerInviteUnread(setHasUnreadPartnerInvite);
  }, []);

  const getPartnerSharedDataText = (partner) => {
    if (Array.isArray(partner?.sharedData)) {
      const validSharedData = partner.sharedData
        .map((item) => String(item || "").trim())
        .filter(Boolean);

      return validSharedData.length ? validSharedData.join(", ") : "None";
    }

    // Fallback for older API responses or locally created partner objects.
    const sharedItems = [
      partner?.shareProfiles ? "Profiles" : null,
      partner?.shareJobRoles ? "Job Roles" : null,
      partner?.shareDepartments ? "Departments" : null,
    ].filter(Boolean);

    return sharedItems.length ? sharedItems.join(", ") : "None";
  };

  const getByPartnerSharedDataText = (partner) => {
    if (Array.isArray(partner?.partnerSharedData)) {
      const validSharedData = partner.partnerSharedData
        .map((item) => String(item || "").trim())
        .filter(Boolean);

      return validSharedData.length ? validSharedData.join(", ") : "None";
    }

    // Fallback for older API responses or locally created partner objects.
    const sharedItems = [
      partner?.shareProfiles ? "Profiles" : null,
      partner?.shareJobRoles ? "Job Roles" : null,
      partner?.shareDepartments ? "Departments" : null,
    ].filter(Boolean);

    return sharedItems.length ? sharedItems.join(", ") : "None";
  };

  const isExpiredPartnerInvitation = (partner) => {
    const rawStatus = String(
      partner?.rawStatus ||
        partner?.connectionStatus ||
        partner?.status ||
        "",
    )
      .trim()
      .toUpperCase();

    return rawStatus === "EXPIRED";
  };

  const isSentPartnerInvitation = (partner) => {
    const invitation = String(partner?.invitation || "")
      .trim()
      .toUpperCase();

    const direction = String(partner?.direction || "")
      .trim()
      .toUpperCase();

    return invitation === "SENT" || direction === "SENT";
  };

  const getPartnerActionMode = (partner) => {
    const status = String(partner?.status || "")
      .trim()
      .toUpperCase();

    const invitation = String(partner?.invitation || partner?.direction || "")
      .trim()
      .toUpperCase();

    if (status === "ACTIVE") {
      return "manage";
    }

    if (isExpiredPartnerInvitation(partner)) {
      return "track";
    }

    if (status === "PENDING" && invitation === "RECEIVED") {
      return "review";
    }

    if (status === "PENDING" && invitation === "SENT") {
      return "track";
    }

    return "details";
  };

  const getPartnerActionMeta = (partner) => {
    const status = String(partner?.status || "")
      .trim()
      .toUpperCase();

    const invitation = String(partner?.invitation || partner?.direction || "")
      .trim()
      .toUpperCase();

    // ACTIVE always shows Manage
    if (status === "ACTIVE") {
      return {
        label: "Manage",
        helper: "Connection accepted",
      };
    }

    if (isExpiredPartnerInvitation(partner)) {
      return {
        label: isSentPartnerInvitation(partner) ? "Resend" : "Details",
        helper: "Invitation expired",
      };
    }

    // PENDING + RECEIVED shows Review
    if (status === "PENDING" && invitation === "RECEIVED") {
      return {
        label: "Review",
        helper: "Invitation received",
      };
    }

    // PENDING + SENT shows Track
    if (status === "PENDING" && invitation === "SENT") {
      return {
        label: "Track",
        helper: "Invitation sent",
      };
    }

    // INACTIVE shows Details
    if (status === "INACTIVE") {
      return {
        label: "Details",
        helper: "Connection inactive",
      };
    }

    return {
      label: "Details",
      helper: "View partner details",
    };
  };

  const getPartnerDirectionMeta = (partner) => {
    const status = String(partner?.status || "")
      .trim()
      .toUpperCase();

    const invitation = String(partner?.invitation || partner?.direction || "")
      .trim()
      .toUpperCase();

    if (status === "INACTIVE") {
      return {
        label: "Connection inactive",
        tone: "inactive",
        icon: <Unlink size={14} />,
      };
    }

    if (status === "PENDING" && invitation === "RECEIVED") {
      return {
        label: "Invitation received",
        tone: "received",
        icon: <ArrowDownLeft size={14} />,
      };
    }

    if (status === "PENDING" && invitation === "SENT") {
      return {
        label: "Invitation sent",
        tone: "sent",
        icon: <ArrowUpRight size={14} />,
      };
    }

    if (status === "ACTIVE") {
      return {
        label: "",
        tone: "",
        icon: null,
      };
    }

    return {
      label: "",
      tone: "",
      icon: null,
    };
  };
  const acceptPartnerInvite = async (partnerId, decision) => {
    try {
      const obj = {
        id: partnerId,
        status: decision,
      };

      const response = await updatePartnerRequestApi(obj);

      if (response?.code === 200) {
        // Refresh the actual directory state.
        await fetchPartnersListApi();

        // Close both the modal content and its overlay.
        setPartnerManageModalOpen(false);
        setPartnerActionMode("");
        setManagingPartner(null);
        setInviteDecision("");
        setDeclineReason("");

        const invitationWasAccepted = ["ACCEPT", "ACCEPTED"].includes(
          String(decision).toUpperCase(),
        );

        MessageManager.showToast(
          "success",
          invitationWasAccepted
            ? "Partner invitation accepted"
            : "Partner invitation declined",
        );
      }
    } catch (error) {
      console.error("Failed to accept partner invite:", error);
      handleApiError(error, { showModal: true });
    }
  };

  /*
   * The API already applies search, filters, and pagination.
   * Only sort the records returned for the current page.
   */
  const visiblePartners = useMemo(() => {
    return [...partners].sort((a, b) => {
      let aValue;
      let bValue;

      if (partnerSort.field === "status") {
        aValue = String(a.status || "").toLowerCase();
        bValue = String(b.status || "").toLowerCase();
      } else {
        aValue = new Date(a.lastUpdated || 0).getTime();
        bValue = new Date(b.lastUpdated || 0).getTime();
      }

      if (aValue === bValue) return 0;
      const comparison = aValue > bValue ? 1 : -1;
      return partnerSort.direction === "asc" ? comparison : -comparison;
    });
  }, [partners, partnerSort]);

  const partnerTotalPages = Math.max(
    1,
    Math.ceil(partnerTotalCount / partnerItemsPerPage),
  );

  const currentPartnerPageIds = visiblePartners
    .filter(
      (partner) =>
        String(partner.status || "")
          .trim()
          .toUpperCase() === "ACTIVE",
    )
    .map((partner) => partner.id);

  const selectedActivePartnerIds = selectedPartnerIds.filter((partnerId) => {
    const selectedPartner = partners.find(
      (partner) => partner.id === partnerId,
    );

    return (
      String(selectedPartner?.status || "")
        .trim()
        .toUpperCase() === "ACTIVE"
    );
  });

  const partnerIdsToRemove = singlePartnerIdToRemove
    ? [singlePartnerIdToRemove]
    : selectedActivePartnerIds;
  const allCurrentPartnersSelected =
    currentPartnerPageIds.length > 0 &&
    currentPartnerPageIds.every((id) => selectedPartnerIds.includes(id));
  const someCurrentPartnersSelected =
    currentPartnerPageIds.some((id) => selectedPartnerIds.includes(id)) &&
    !allCurrentPartnersSelected;

  useEffect(() => {
    if (partnerCurrentPage > partnerTotalPages) {
      setPartnerCurrentPage(partnerTotalPages);
    }
  }, [partnerCurrentPage, partnerTotalPages]);

  const getAppliedPartnerFiltersCount = () => {
    let count = 0;
    if (partnerDirectorySearch) count += 1;
    if (partnerStatusFilter !== "All") count += 1;
    if (partnerDirectionFilter !== "All") count += 1;
    if (partnerSharedDataFilter !== "All") count += 1;
    return count;
  };

  const clearPartnerDirectoryFilters = () => {
    setPartnerDirectorySearchInput("");
    setPartnerDirectorySearch("");
    setPartnerStatusFilter("All");
    setPartnerDirectionFilter("All");
    setPartnerSharedDataFilter("All");
    setShowPartnerDirectorySearch(false);
    setShowPartnerDirectoryFilters(false);
    setPartnerCurrentPage(1);
  };

  const togglePartnerSort = (field) => {
    setPartnerSort((previous) => ({
      field,
      direction:
        previous.field === field && previous.direction === "asc"
          ? "desc"
          : "asc",
    }));
    setPartnerCurrentPage(1);
  };

  const renderPartnerSortChevrons = (field) => (
    <span className="partner-directory-sort-chevrons" aria-hidden="true">
      <ChevronUp
        size={13}
        className={
          partnerSort.field === field && partnerSort.direction === "asc"
            ? "active"
            : ""
        }
      />
      <ChevronDown
        size={13}
        className={
          partnerSort.field === field && partnerSort.direction === "desc"
            ? "active"
            : ""
        }
      />
    </span>
  );

  const togglePartnerSelection = (partnerId, checked) => {
    setSelectedPartnerIds((previous) =>
      checked
        ? Array.from(new Set([...previous, partnerId]))
        : previous.filter((id) => id !== partnerId),
    );
  };

  const toggleAllPartnersOnCurrentPage = (checked) => {
    setSelectedPartnerIds((previous) =>
      checked
        ? Array.from(new Set([...previous, ...currentPartnerPageIds]))
        : previous.filter((id) => !currentPartnerPageIds.includes(id)),
    );
  };

  const handleBulkRemoveConnections = async () => {
    if (!partnerIdsToRemove.length) {
      setShowPartnerBulkDeleteModal(false);
      setSinglePartnerIdToRemove(null);

      MessageManager.showToast(
        "warning",
        "Select at least one active partner connection.",
      );

      return;
    }

    const changedAt = new Date().toISOString();

    /*
     * This remains the existing local placeholder.
     * Replace only this state update when a bulk-remove API
     * becomes available.
     */
    // setPartners((previous) =>
    //   previous.map((partner) =>
    //     selectedActivePartnerIds.includes(partner.id)
    //       ? {
    //           ...partner,
    //           status: "Inactive",
    //           invitation: "REMOVED",
    //           direction: "",
    //           shareProfiles: false,
    //           protectProfileIdentity: false,
    //           shareJobRoles: false,
    //           shareDepartments: false,
    //           inactiveReason: "Connection Removed",
    //           inactiveDescription:
    //             "The partner connection was removed by your organization.",
    //           inactiveStatusChangedAt: changedAt,
    //           inactiveResponsibleOrganization:
    //             orgInfos.org_name || "",
    //           lastUpdated: changedAt,
    //         }
    //       : partner,
    //   ),
    // );

    const response = await removePartnerConnectionApi({
      partnerIds: partnerIdsToRemove,
    });
    if (response.code == 200) {
      fetchPartnersListApi();

      MessageManager.showToast(
        "success",
        "Partner connection removed",
      );
    }

    setSelectedPartnerIds([]);
    setSinglePartnerIdToRemove(null);
    setShowPartnerBulkDeleteModal(false);
  };

  const defaultPartnerForm = {
    organizationCode: "",
    name: "",
    contactPerson: "",
    contactEmail: "",
    shareProfiles: false,
    protectProfileIdentity: true,
    shareJobRoles: false,
    shareDepartments: false,
    status: "Pending",
    sharedProfiles: 0,
    lastUpdated: "",
    direction: "sent",
    dateSent: "",
    sentBy: "",
    sentByEmail: "",
    sharedProfileIds: [],
    sharedJobRoleIds: [],
    inactiveReason: "",
    _id: null,
  };

  const [partnerViewModalOpen, setPartnerViewModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [editTenantId, setEditTenantId] = useState(null);
  const [addPartnerModalOpen, setAddPartnerModalOpen] = useState(false);
  const [partnerCodeInfoOpen, setPartnerCodeInfoOpen] = useState(false);
  const [partnerManageModalOpen, setPartnerManageModalOpen] = useState(false);
  const [partnerActionMode, setPartnerActionMode] = useState("");
  const [managingPartner, setManagingPartner] = useState(null);
  const [manageActiveTab, setManageActiveTab] = useState("profiles");
  const [inviteDecision, setInviteDecision] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const [isPartnerDetailsLoading, setIsPartnerDetailsLoading] = useState(false);

  const [manageProfileSearch, setManageProfileSearch] = useState("");
  const [manageProfileDepartmentFilter, setManageProfileDepartmentFilter] =
    useState("All");
  const [manageProfileLevelFilter, setManageProfileLevelFilter] =
    useState("All");
  const [manageProfileAvailabilityFilter, setManageProfileAvailabilityFilter] =
    useState("All");
  const [showManageProfileSearch, setShowManageProfileSearch] = useState(false);
  const [showManageProfileFilters, setShowManageProfileFilters] =
    useState(false);
  const [selectedManagedProfileIds, setSelectedManagedProfileIds] = useState(
    [],
  );

  const [manageJobSearch, setManageJobSearch] = useState("");
  const [manageJobDepartmentFilter, setManageJobDepartmentFilter] =
    useState("All");
  const [manageJobLevelFilter, setManageJobLevelFilter] = useState("All");
  const [showManageJobSearch, setShowManageJobSearch] = useState(false);
  const [showManageJobFilters, setShowManageJobFilters] = useState(false);
  const [selectedManagedJobRoleIds, setSelectedManagedJobRoleIds] = useState(
    [],
  );
  const [stopSharingMode, setStopSharingMode] = useState("");
  const [capabilityWarningMode, setCapabilityWarningMode] = useState("");

  const [newPartner, setNewPartner] = useState(defaultPartnerForm);
  const [isSendingPartnerInvite, setIsSendingPartnerInvite] = useState(false);

  const isPartnerFormValid = (partner) =>
    Boolean(
      partner.organizationCode.trim() &&
      partner.name.trim() &&
      partner.contactPerson.trim() &&
      partner.contactEmail.trim(),
    );

  const getUniquePartnerOptions = (items, key) => [
    "All",
    ...Array.from(new Set(items.map((item) => item[key]).filter(Boolean))),
  ];

  const updatePartnerInDirectory = (updatedPartner, successMessage) => {
    setPartners((prevPartners) =>
      prevPartners.map((partner) =>
        partner.id === updatedPartner.id ? updatedPartner : partner,
      ),
    );

    setSelectedPartner((prevPartner) =>
      prevPartner?.id === updatedPartner.id ? updatedPartner : prevPartner,
    );

    setManagingPartner((prevPartner) =>
      prevPartner?.id === updatedPartner.id ? updatedPartner : prevPartner,
    );

    if (successMessage) {
      MessageManager.showToast("success", successMessage);
    }
  };

  const resetManageFilters = () => {
    setManageProfileSearch("");
    setManageProfileDepartmentFilter(profileAllOption);
    setManageProfileLevelFilter(profileAllOption);
    setManageProfileAvailabilityFilter(profileAllOption);
    setSelectedManagedProfileIds([]);
    setShowManageProfileSearch(false);
    setShowManageProfileFilters(false);

    setManageJobSearch("");
    setManageJobDepartmentFilter("All");
    setManageJobLevelFilter("All");
    setSelectedManagedJobRoleIds([]);
    setShowManageJobSearch(false);
    setShowManageJobFilters(false);
  };

  const getAppliedManageProfileFiltersCount = () =>
    [
      Boolean(manageProfileSearch.trim()),
      manageProfileDepartmentFilter !== profileAllOption,
      manageProfileLevelFilter !== profileAllOption,
      manageProfileAvailabilityFilter !== profileAllOption,
    ].filter(Boolean).length;

  const clearManageProfileFilters = () => {
    setManageProfileSearch("");
    setManageProfileDepartmentFilter(profileAllOption);
    setManageProfileLevelFilter(profileAllOption);
    setManageProfileAvailabilityFilter(profileAllOption);
    setShowManageProfileSearch(false);
    setShowManageProfileFilters(false);
  };

  const getAppliedManageJobFiltersCount = () =>
    [
      Boolean(manageJobSearch.trim()),
      manageJobDepartmentFilter !== "All",
      manageJobLevelFilter !== "All",
    ].filter(Boolean).length;

  const clearManageJobFilters = () => {
    setManageJobSearch("");
    setManageJobDepartmentFilter("All");
    setManageJobLevelFilter("All");
    setShowManageJobSearch(false);
    setShowManageJobFilters(false);
  };

  // const openAddPartnerModal = () => {
  //   setNewPartner(defaultPartnerForm);
  //   setInputOrganizationCode("");
  //   setTenantId(null);
  //   setPartnerCodeInfoOpen(false);
  //   setAddPartnerModalOpen(true);
  // };

  const openAddPartnerModal = () => {
    setNewPartner(defaultPartnerForm);
    setInputOrganizationCode("");
    setTenantId(null);
    setPartnerCodeError("");
    setPartnerCodeInfoOpen(false);
    setAddPartnerModalOpen(true);
  };
  const [inputOrganizationCode, setInputOrganizationCode] = useState("");
  const [tenantId, setTenantId] = useState(null);
  const [partnerCodeError, setPartnerCodeError] = useState("");

  const handlePartnerCodeChange = async (setPartnerState) => {
    const organizationCode = inputOrganizationCode.trim();

    setPartnerCodeError("");
    setPartnerCodeInfoOpen(false);
    setTenantId(null);

    if (!organizationCode) {
      setPartnerCodeError("Please enter an organization code.");
      return;
    }

    try {
      const response = await getAdminDetailsByOrgCodeapi(organizationCode);

      const orgData = response?.data || response;

      if (!orgData?.tenantId) {
        setPartnerState((prevPartner) => ({
          ...prevPartner,
          organizationCode: "",
          name: "",
          contactPerson: "",
          contactEmail: "",
        }));

        setPartnerCodeError(
          response?.message || "Organization details were not found.",
        );
        return;
      }

      setPartnerState((prevPartner) => ({
        ...prevPartner,
        organizationCode: orgData?.orgCode || organizationCode,
        name: orgData?.organization || "",
        contactPerson: orgData?.adminName || "",
        contactEmail: orgData?.adminEmail || "",
      }));

      setTenantId(orgData.tenantId);
      setPartnerCodeInfoOpen(true);
      setPartnerCodeError("");
    } catch (error) {
      console.error("Error fetching admin details:", error);

      const apiErrorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "Unable to validate the organization code.";

      setPartnerState((prevPartner) => ({
        ...prevPartner,
        organizationCode: "",
        name: "",
        contactPerson: "",
        contactEmail: "",
      }));

      setTenantId(null);
      setPartnerCodeInfoOpen(false);
      setPartnerCodeError(apiErrorMessage);

      // Keep special subscription handling if required.
      if (error?.response?.status === 403) {
        handleApiError(error, { showModal: false });
      }
    }
  };
  const handleSavePartner = async () => {
    if (isSendingPartnerInvite) return;

    if (!isPartnerFormValid(newPartner)) {
      MessageManager.showToast(
        "error",
        "Please select a valid partner organization code.",
      );
      return;
    }

    setIsSendingPartnerInvite(true);

    try {
      const obj = {
        partnerTenantId: tenantId,
        profileShare: newPartner.shareProfiles,
        profiles: newPartner.sharedProfileIds || [],
        protectProfileIdentity: newPartner.protectProfileIdentity,
        jobRolesShare: newPartner.shareJobRoles,
        jobRoles: newPartner.sharedJobRoleIds || [],
        departmentShare: newPartner.shareDepartments,
        departments: newPartner.sharedDepartments || [],
      };

      const response = await addPartnerApi(obj);

      if (response?.code === 200) {
        if (response?.data == null) {
          MessageManager.showToast(
            "error",
            "Partner already exists in your directory.",
          );
          return;
        }
        setAddPartnerModalOpen(false);
        setNewPartner(defaultPartnerForm);
        setPartnerCodeInfoOpen(false);
        setInputOrganizationCode("");
        setTenantId(null);

        MessageManager.showToast("success", "Partner invite sent");

        await fetchPartnersListApi();
      }
    } catch (error) {
      console.error("Error adding partner:", error);
      handleApiError(error, { showModal: true });
    } finally {
      setIsSendingPartnerInvite(false);
    }
  };
  const handleSavePartnerView = async () => {
    if (!selectedPartner || !isPartnerFormValid(selectedPartner)) return;

    const profileUserIds = getProfileUserIds(
      selectedPartner.sharedProfileIds || [],
      selectedPartner.profileOptions || [],
    );

    try {
      const obj = {
        id: editTenantId,
        partnerData: {
          profileShare: selectedPartner.shareProfiles,
          profiles: selectedPartner.shareProfiles ? profileUserIds : [],
          protectProfileIdentity: selectedPartner.shareProfiles
            ? selectedPartner.protectProfileIdentity
            : false,
          jobRolesShare: selectedPartner.shareJobRoles,
          jobRoles: selectedPartner.shareJobRoles
            ? selectedPartner.sharedJobRoleIds || []
            : [],
          departmentShare: selectedPartner.shareDepartments,
          departments: selectedPartner.shareDepartments
            ? selectedPartner.sharedDepartments || []
            : [],
        },
      };

      await editPartnerApi(obj);

      MessageManager.showToast("success", "Changes saved");

      setPartnerViewModalOpen(false);
      fetchPartnersListApi();
    } catch (error) {
      console.error("Error updating partner:", error);
      handleApiError(error, { showModal: true });
    }
  };

  const resendPartnerInvitation = async (partner) => {
    const partnerId =
      typeof partner === "string" ? partner : partner?._id || partner?.id;

    let obj = {
      id: partnerId,
    };
    let response = await resendInvitationApi(obj);
    if (response.code === 200) {
      fetchPartnersListApi();
      // Close both the modal content and its overlay.
      setPartnerManageModalOpen(false);
      setPartnerActionMode("");
      setManagingPartner(null);
      setInviteDecision("");
      setDeclineReason("");
      MessageManager.showToast("success", "Partner invite sent");
    }
  };

  const handleCancelPartnerInvitation = (partner) => {
    if (!partner) return;

    const changedAt = new Date().toISOString();

    updatePartnerInDirectory(
      {
        ...partner,
        status: "Inactive",
        invitation: "CANCELLED",
        direction: "",
        shareProfiles: false,
        protectProfileIdentity: false,
        shareJobRoles: false,
        shareDepartments: false,
        inactiveReason: "Invitation Cancelled",
        inactiveDescription:
          "The pending invitation was cancelled by your organization.",
        inactiveStatusChangedAt: changedAt,
        inactiveResponsibleOrganization: orgInfos.org_name || "",
        lastUpdated: changedAt,
      },
      "Partner invite cancelled",
    );

    setPartnerManageModalOpen(false);
    setPartnerActionMode("");
    setManagingPartner(null);
  };

  const handleRemovePartnerConnection = (partner) => {
    if (!partner) return;

    updatePartnerInDirectory(
      {
        ...partner,
        status: "INACTIVE",
        inactiveReason: "Connection removed by your organization.",
        lastUpdated: "Just now",
      },
      "Connection removed.",
    );

    setPartnerViewModalOpen(false);
    setPartnerManageModalOpen(false);
  };

  const openPartnerActionModal = async (partnerRow) => {
    if (isPartnerDetailsLoading) return;

    const partnerId =
      partnerRow?._id || partnerRow?.id || partnerRow?.partnerId || "";

    if (!partnerId) {
      MessageManager.showToast("error", "Partner ID is missing.");
      return;
    }

    setInviteDecision("");
    setDeclineReason("");
    resetManageFilters();

    // Clear previously opened partner data so list data or stale popup data
    // cannot appear while the latest detail request is running.
    setManagingPartner(null);
    setSelectedPartner(null);
    setPartnerActionMode("");
    setPartnerManageModalOpen(false);
    setPartnerViewModalOpen(false);
    setIsPartnerDetailsLoading(true);

    try {
      // The directory row is used only for the partner id. Every popup action
      // is populated exclusively from getPartnerByIdApi.
      const partnerDetails = await fetchPartnerById(partnerId);

      if (!partnerDetails) return;

      const actionMode = getPartnerActionMode(partnerDetails);

      if (actionMode === "details") {
        setSelectedPartner(partnerDetails);
        setEditTenantId(partnerDetails._id || partnerDetails.id || null);
        setPartnerViewModalOpen(true);
        return;
      }

      setPartnerActionMode(actionMode);
      setManagingPartner(partnerDetails);
      setManageActiveTab(
        partnerDetails.shareProfiles ? "profiles" : "jobRoles",
      );
      setPartnerManageModalOpen(true);
    } finally {
      setIsPartnerDetailsLoading(false);
    }
  };

  const handleSaveManagedSharing = async () => {
    if (!managingPartner) return;

    const partnerId = managingPartner._id || managingPartner.id;

    if (!partnerId) {
      MessageManager.showToast("error", "Partner ID is missing.");
      return;
    }

    const profileUserIds = getProfileUserIds(
      managingPartner.sharedProfileIds || [],
      managingPartner.profileOptions || [],
    );

    try {
      const obj = {
        id: partnerId,
        partnerData: {
          profileShare: Boolean(managingPartner.shareProfiles),

          profiles: managingPartner.shareProfiles ? profileUserIds : [],

          protectProfileIdentity: managingPartner.shareProfiles
            ? Boolean(managingPartner.protectProfileIdentity)
            : false,

          jobRolesShare: Boolean(managingPartner.shareJobRoles),
          jobRoles: managingPartner.shareJobRoles
            ? managingPartner.sharedJobRoleIds || []
            : [],

          departmentShare: Boolean(managingPartner.shareDepartments),
          departments: managingPartner.shareDepartments
            ? managingPartner.sharedDepartments || []
            : [],
        },
      };

      await editPartnerApi(obj);

      MessageManager.showToast("success", "Changes saved");
      setPartnerManageModalOpen(false);
      fetchPartnersListApi();
    } catch (error) {
      console.error("Error updating managed sharing:", error);
      handleApiError(error, { showModal: true });
    }
  };
  const handleReviewInviteLater = () => {
    setPartnerManageModalOpen(false);
    MessageManager.showToast("success", "Invitation saved for later.");
  };

  const closePartnerActionModal = () => {
    setPartnerManageModalOpen(false);
    setPartnerActionMode("");
    setManagingPartner(null);
    setInviteDecision("");
    setDeclineReason("");
  };

  const getPartnerActionModalTitle = () => {
    if (partnerActionMode === "manage") {
      return "Manage Sharing";
    }

    if (partnerActionMode === "review") {
      return "Review Invitation";
    }

    if (partnerActionMode === "track") {
      return "Track Invitation";
    }

    return "Partner Details";
  };

  const toggleManagedProfileSelection = (profileId, checked) => {
    setSelectedManagedProfileIds((previousIds) =>
      checked
        ? Array.from(new Set([...previousIds, profileId]))
        : previousIds.filter((id) => id !== profileId),
    );
  };

  const toggleAllManagedProfiles = (checked) => {
    const visibleIds = filteredManageProfiles.map((profile) => profile.id);

    setSelectedManagedProfileIds((previousIds) =>
      checked
        ? Array.from(new Set([...previousIds, ...visibleIds]))
        : previousIds.filter((id) => !visibleIds.includes(id)),
    );
  };

  const handleBulkRemoveManagedProfiles = () => {
    if (!selectedManagedProfileIds.length) return;

    setStopSharingMode("profiles");
  };

  const toggleManagedJobSelection = (jobId, checked) => {
    setSelectedManagedJobRoleIds((previousIds) =>
      checked
        ? Array.from(new Set([...previousIds, jobId]))
        : previousIds.filter((id) => id !== jobId),
    );
  };

  const toggleAllManagedJobs = (checked) => {
    const visibleIds = filteredManageJobRoles.map((role) => role.id);

    setSelectedManagedJobRoleIds((previousIds) =>
      checked
        ? Array.from(new Set([...previousIds, ...visibleIds]))
        : previousIds.filter((id) => !visibleIds.includes(id)),
    );
  };

  const handleBulkRemoveManagedJobRoles = () => {
    if (!selectedManagedJobRoleIds.length) return;

    setStopSharingMode("jobRoles");
  };

  const confirmStopSharingSelectedItems = () => {
    if (stopSharingMode === "profiles") {
      const profileIdsToRemove = [...selectedManagedProfileIds];

      setManagingPartner((previousPartner) => ({
        ...previousPartner,
        sharedProfileIds: (previousPartner?.sharedProfileIds || []).filter(
          (id) => !profileIdsToRemove.includes(id),
        ),
      }));

      setSelectedManagedProfileIds([]);
    }

    if (stopSharingMode === "jobRoles") {
      const jobRoleIdsToRemove = [...selectedManagedJobRoleIds];

      setManagingPartner((previousPartner) => ({
        ...previousPartner,
        sharedJobRoleIds: (previousPartner?.sharedJobRoleIds || []).filter(
          (id) => !jobRoleIdsToRemove.includes(id),
        ),
      }));

      setSelectedManagedJobRoleIds([]);
    }

    setStopSharingMode("");
  };

  const handleManagedCapabilityToggle = (type, checked) => {
    // Enabling is non-destructive, so no warning is needed.
    if (checked) {
      setManagingPartner((previousPartner) => ({
        ...previousPartner,
        ...(type === "profiles"
          ? { shareProfiles: true }
          : { shareJobRoles: true }),
      }));
      return;
    }

    // Disabling is destructive, so ask for confirmation first.
    setCapabilityWarningMode(type);
  };

  const confirmDisableManagedCapability = () => {
    setManagingPartner((previousPartner) => {
      if (!previousPartner) return previousPartner;

      if (capabilityWarningMode === "profiles") {
        return {
          ...previousPartner,
          shareProfiles: false,
          protectProfileIdentity: false,
          sharedProfileIds: [],
        };
      }

      if (capabilityWarningMode === "jobRoles") {
        return {
          ...previousPartner,
          shareJobRoles: false,
          shareDepartments: false,
          sharedJobRoleIds: [],
        };
      }

      return previousPartner;
    });

    if (capabilityWarningMode === "profiles") {
      setSelectedManagedProfileIds([]);
    }

    if (capabilityWarningMode === "jobRoles") {
      setSelectedManagedJobRoleIds([]);
    }

    setCapabilityWarningMode("");
  };

  const getItemId = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    return item._id || item.id || item.userId || "";
  };

  const getProfileUserId = (profile) => {
    if (!profile) return "";

    // If backend already gave a string array, assume it is already the userId.
    if (typeof profile === "string") return profile;

    // For profile objects, API needs userId, not _id.
    return profile.userId || "";
  };

  const getProfileUserIds = (profileIds = [], profileOptions = []) => {
    const userIdMap = new Map(
      profileOptions
        .filter((profile) => profile?.id && profile?.userId)
        .map((profile) => [profile.id, profile.userId]),
    );

    return profileIds.map((id) => userIdMap.get(id) || id).filter(Boolean);
  };

  const normalizeManagedProfile = (profile = {}) => {
    const userId = profile.userId || "";
    const id = userId || profile.id || profile._id || "";

    return {
      id,
      userId,
      profileObjectId: profile._id || profile.id || "",
      protectedId:
        profile.externalCode ||
        profile.protectedId ||
        `PROFILE-${String(id).slice(-6).toUpperCase()}`,
      name: profile.name || "-",
      email: profile.email || profile.userId || "-",
      jobRole:
        profile.designation || profile.jobRole || profile.roleName || "-",
      department: profile.department || "-",
      level:
        profile.level ||
        profile.levelName ||
        profile.seniority ||
        profile.experienceLevel ||
        "-",
      availability: profile.availability || "Unknown",
    };
  };
  const normalizeManagedJobRole = (role = {}) => {
    const id = role.jobId || role._id || role.id || role.jobRoleId || "";

    return {
      id,
      jobId: role.jobId || "",
      name: role.roleName || role.name || role.jobRoleName || "-",
      roleName: role.roleName || role.name || role.jobRoleName || "-",
      department: role.department || "-",
      level: role.level || "-",
      hiringStatus: role.hiringStatus || role.status || "Not Set",
      createdAt: role.createdAt || role.createdDate || role.updatedAt || "",
      shortDescription: role.shortDescription || role.description || "",
      visibility: role.visibility || "",
    };
  };
  const normalizePartnerDetails = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const partner =
      responseData?.partner ?? responseData?.data ?? responseData ?? {};

    const profileOptions = Array.isArray(partner?.profiles)
      ? partner.profiles.map(normalizeManagedProfile)
      : [];

    const jobRoleOptions = Array.isArray(partner?.jobRoles)
      ? partner.jobRoles.map(normalizeManagedJobRole)
      : [];

    const normalizedStatus = normalizePartnerStatus(
      partner?.status || partner?.connectionStatus,
    );

    const normalizedDirection = normalizePartnerDirection(
      partner,
      normalizedStatus,
    );

    const partnerId = partner?._id || partner?.id || partner?.partnerId || "";

    return {
      ...partner,

      id: partnerId,
      _id: partnerId,

      name:
        partner?.partnerOrganization ||
        partner?.organizationName ||
        partner?.name ||
        "",

      organizationCode:
        partner?.partnerOrgCode ||
        partner?.organizationCode ||
        partner?.orgCode ||
        "",

      contactPerson:
        partner?.partnerSuperAdminName ||
        partner?.contactPerson ||
        partner?.adminName ||
        "",

      contactEmail:
        partner?.partnerSuperAdminEmail ||
        partner?.contactEmail ||
        partner?.adminEmail ||
        "",

      sentBy:
        partner?.sentBy ||
        partner?.senderName ||
        partner?.invitedByName ||
        partner?.createdByName ||
        "",

      sentByEmail:
        partner?.sentByEmail ||
        partner?.senderEmail ||
        partner?.invitedByEmail ||
        partner?.createdByEmail ||
        "",

      rawStatus: String(
        partner?.status || partner?.connectionStatus || "",
      )
        .trim()
        .toUpperCase(),

      status: normalizedStatus,

      invitation: String(partner?.invitation || "")
        .trim()
        .toUpperCase(),

      direction: normalizedDirection,

      dateSent:
        partner?.dateSent ||
        partner?.invitedDate ||
        partner?.invitedAt ||
        partner?.createdDate ||
        partner?.createdAt ||
        "",

      expiredAt:
        partner?.expiredAt ||
        partner?.expiresAt ||
        partner?.invitationExpiresAt ||
        "",

      lastUpdated:
        partner?.updatedDate ||
        partner?.updatedAt ||
        partner?.createdDate ||
        partner?.createdAt ||
        "",

      shareProfiles: normalizeBoolean(
        partner?.profileShare ?? partner?.shareProfiles,
      ),

      protectProfileIdentity: normalizeBoolean(partner?.protectProfileIdentity),

      shareJobRoles: normalizeBoolean(
        partner?.jobRolesShare ?? partner?.shareJobRoles,
      ),

      shareDepartments: normalizeBoolean(
        partner?.departmentShare ?? partner?.shareDepartments,
      ),

      sharedProfileIds: profileOptions
        .map((profile) => profile.userId || profile.id)
        .filter(Boolean),

      sharedJobRoleIds: jobRoleOptions.map((role) => role.id).filter(Boolean),

      sharedDepartments: Array.isArray(partner?.departments)
        ? partner.departments.map(getDepartmentValue).filter(Boolean)
        : [],

      profileOptions,
      jobRoleOptions,

      inactiveReason:
        partner?.inactiveReason ||
        partner?.declineReason ||
        partner?.removedReason ||
        "",

      inactiveDescription:
        partner?.inactiveDescription ||
        partner?.declineReason ||
        partner?.removalDescription ||
        partner?.removedReason ||
        "",
    };
  };

  const fetchPartnerById = async (partnerId) => {
    if (!partnerId) {
      MessageManager.showToast("error", "Partner ID is missing.");
      return null;
    }

    try {
      const response = await getPartnerByIdApi(partnerId);
      const normalizedPartner = normalizePartnerDetails(response);

      if (!normalizedPartner?.id) {
        MessageManager.showToast("error", "Partner details were not found.");
        return null;
      }

      return normalizedPartner;
    } catch (error) {
      console.error("Error fetching partner by ID:", error);
      handleApiError(error, { showModal: false });
      return null;
    }
  };

  const manageProfileOptions = managingPartner?.profileOptions || [];

  const sharedManageProfileOptions = manageProfileOptions.filter((profile) =>
    (managingPartner?.sharedProfileIds || []).includes(profile.id),
  );

  const filteredManageProfiles = sharedManageProfileOptions.filter(
    (profile) => {
      const searchValue = manageProfileSearch.trim().toLowerCase();

      const searchableText = [
        profile.id,
        profile.protectedId,
        profile.name,
        profile.email,
        profile.jobRole,
        profile.department,
        profile.level,
        profile.availability,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        (!searchValue || searchableText.includes(searchValue)) &&
        (manageProfileDepartmentFilter === profileAllOption ||
          profile.department === manageProfileDepartmentFilter) &&
        (manageProfileLevelFilter === profileAllOption ||
          profile.level === manageProfileLevelFilter) &&
        (manageProfileAvailabilityFilter === profileAllOption ||
          profile.availability === manageProfileAvailabilityFilter)
      );
    },
  );

  const manageJobRoleOptions = managingPartner?.jobRoleOptions || [];

  const sharedManageJobRoleOptions = manageJobRoleOptions.filter((role) =>
    (managingPartner?.sharedJobRoleIds || []).includes(role.id),
  );

  const jobRoleDepartmentOptions = [
    {
      value: "All",
      label: t("common.all"),
    },
    ...Array.from(
      new Set(
        sharedManageJobRoleOptions
          .map((role) => role.department)
          .filter(Boolean)
          .filter((department) => department !== "-"),
      ),
    ).map((departmentName) => ({
      value: departmentName,
      label: departmentName,
    })),
  ];

  const filteredManageJobRoles = sharedManageJobRoleOptions.filter((role) => {
    const searchValue = manageJobSearch.trim().toLowerCase();

    const searchableText = [role.name, role.department, role.level]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      (!searchValue || searchableText.includes(searchValue)) &&
      (manageJobDepartmentFilter === "All" ||
        role.department === manageJobDepartmentFilter) &&
      (manageJobLevelFilter === "All" || role.level === manageJobLevelFilter)
    );
  });

  useEffect(() => {
    if (!managingPartner) return;

    if (
      manageActiveTab === "profiles" &&
      !managingPartner.shareProfiles &&
      managingPartner.shareJobRoles
    ) {
      setManageActiveTab("jobRoles");
    }

    if (
      manageActiveTab === "jobRoles" &&
      !managingPartner.shareJobRoles &&
      managingPartner.shareProfiles
    ) {
      setManageActiveTab("profiles");
    }
  }, [
    managingPartner?.shareProfiles,
    managingPartner?.shareJobRoles,
    manageActiveTab,
  ]);

  const visibleManagedProfileIds = filteredManageProfiles.map(
    (profile) => profile.id,
  );

  const isAllVisibleProfilesSelected =
    visibleManagedProfileIds.length > 0 &&
    visibleManagedProfileIds.every((id) =>
      selectedManagedProfileIds.includes(id),
    );

  const someVisibleProfilesSelected =
    visibleManagedProfileIds.some((id) =>
      selectedManagedProfileIds.includes(id),
    ) && !isAllVisibleProfilesSelected;

  const visibleManagedJobRoleIds = filteredManageJobRoles.map(
    (role) => role.id,
  );

  const isAllVisibleJobsSelected =
    visibleManagedJobRoleIds.length > 0 &&
    visibleManagedJobRoleIds.every((id) =>
      selectedManagedJobRoleIds.includes(id),
    );

  const someVisibleJobRolesSelected =
    visibleManagedJobRoleIds.some((id) =>
      selectedManagedJobRoleIds.includes(id),
    ) && !isAllVisibleJobsSelected;

  const renderPartnerToggle = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      className={`partner-switch ${checked ? "on" : ""}`}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span />
    </button>
  );

  const renderPartnerReadOnlyInfo = (partnerState, openByDefault = false) => {
    const shouldShowInfo = isPartnerFormValid(partnerState);
    if (!shouldShowInfo) return null;

    const isOpen = openByDefault || partnerCodeInfoOpen;

    return (
      <div className="partner-info-collapse">
        <button
          type="button"
          className="partner-info-collapse-header"
          onClick={() =>
            !openByDefault && setPartnerCodeInfoOpen((value) => !value)
          }
        >
          <span>This is your information</span>
          {!openByDefault && (
            <strong
              className="partner-info-visibility-icon"
              title={isOpen ? "Hide details" : "Show details"}
              aria-label={isOpen ? "Hide details" : "Show details"}
            >
              {isOpen ? <EyeOff size={16} /> : <Eye size={16} />}
            </strong>
          )}
        </button>

        {isOpen && (
          <div className="partner-readonly-details-grid">
            <div className="partner-readonly-field">
              <span>Partner Organization</span>
              <strong>{partnerState.name}</strong>
            </div>

            <div className="partner-readonly-field">
              <span>Partner Super Admin Name</span>
              <strong>{partnerState.contactPerson}</strong>
            </div>

            <div className="partner-readonly-field">
              <span>Partner Super Admin Email</span>
              <strong>{partnerState.contactEmail}</strong>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPartnerInvitationNote = (partnerState, mode = "view") => {
    if (mode === "add") {
      return (
        <div className="partner-invitation-note">
          <strong>Invitation note</strong>

          <p>
            An invitation email will be sent to the partner's Super Admin.
            Invitation Expires 14 days after it is sent.
          </p>
        </div>
      );
    }

    if (!partnerState?.dateSent) return null;

    return (
      <div className="partner-invitation-note">
        <strong>
          {String(partnerState.status || "").toUpperCase() === "PENDING"
            ? "Invitation Sent"
            : "Invitation Details"}
        </strong>

        <p>
          Sent on {formatPartnerDate(partnerState.dateSent)}. Invitation Expires
          on {formatPartnerDate(getInviteExpiryDate(partnerState.dateSent))}.
        </p>
      </div>
    );
  };

  const renderPartnerFormFields = (
    partnerState,
    setPartnerState,
    options = {},
  ) => {
    const {
      mode = "add",
      allowCodeEdit = false,
      showInvitationNote = false,
    } = options;

    // const codeValue = partnerState.organizationCode.trim();
    // const hasTypedCode = codeValue.length > 0;
    // const isInvalidCode =
    //   allowCodeEdit && hasTypedCode && !isPartnerFormValid(partnerState);
    return (
      <>
        {mode === "add" && (
          <div className="partner-code-onboarding-guide">
            <div className="partner-code-onboarding-heading">
              <span className="partner-code-onboarding-icon">
                <Handshake size={18} />
              </span>

              <div>
                <strong>Connect using an organization code</strong>
                <p>
                  Ask the partner organizationâ€™s administrator to share their
                  organization code before starting this invitation.
                </p>
              </div>
            </div>

            <div className="partner-code-onboarding-steps">
              <div className="partner-code-onboarding-step">
                <span>1</span>
                <div>
                  <strong>Request the code</strong>
                  <p>
                    The partner can find and share their code from their
                    organization settings.
                  </p>
                </div>
              </div>

              <div className="partner-code-onboarding-step">
                <span>2</span>
                <div>
                  <strong>Verify the organization</strong>
                  <p>
                    Enter the code below and use Search to confirm the
                    organization and its Super Admin.
                  </p>
                </div>
              </div>

              <div className="partner-code-onboarding-step">
                <span>3</span>
                <div>
                  <strong>Send the invitation</strong>
                  <p>
                    Sharing will not begin until the partner accepts the
                    invitation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mmh-form-group partner-code-form-group">
          <label className="mmh-form-label">
            Partner Organization Code <span style={{ color: "red" }}>*</span>
            <span
              className="partner-code-tooltip"
              title="Ask your partner to share their code (located in My Organization Settings)"
            >
              <Info size={14} />
            </span>
          </label>

          {allowCodeEdit ? (
            <>
              {/* <input
                type="text"
                list="partner-organization-code-options"
                className="mmh-form-input"
                placeholder="Search or enter partner organization code"
                value={partnerState.organizationCode}
                onChange={(e) =>
                  handlePartnerCodeChange(e.target.value, setPartnerState)
                }
              /> */}
              <span className="partner-code-input-wrapper">
                {/* <input
                  type="text"
                  className="mmh-form-input"
                  value={inputOrganizationCode}
                  onChange={(e) => setInputOrganizationCode(e.target.value)}
                /> */}

                <input
                  type="text"
                  className="mmh-form-input"
                  value={inputOrganizationCode}
                  onChange={(e) => {
                    setInputOrganizationCode(e.target.value);
                    setPartnerCodeError("");
                    setPartnerCodeInfoOpen(false);
                    setTenantId(null);

                    setPartnerState((prevPartner) => ({
                      ...prevPartner,
                      organizationCode: "",
                      name: "",
                      contactPerson: "",
                      contactEmail: "",
                    }));
                  }}
                />
                <button
                  className="mmh-btn mmh-btn-primary"
                  type="button"
                  onClick={() => handlePartnerCodeChange(setPartnerState)}
                >
                  <Search size={16} />
                </button>
              </span>

              {/* <datalist id="partner-organization-code-options">
                {partnerOrganizationDirectory.map((org) => (
                  <option
                    key={org.organizationCode}
                    value={org.organizationCode}
                  >
                    {org.name}
                  </option>
                ))}
              </datalist> */}

              {/* {isInvalidCode && (
                <div className="partner-code-error">
                  This org code does not exist in our database
                </div>
              )} */}
              {partnerCodeError && (
                <div className="partner-code-error" role="alert">
                  {partnerCodeError}
                </div>
              )}
            </>
          ) : (
            <div className="partner-readonly-code">
              {partnerState.organizationCode || "-"}
            </div>
          )}
        </div>

        {renderPartnerReadOnlyInfo(partnerState, mode !== "add")}

        {showInvitationNote && renderPartnerInvitationNote(partnerState, mode)}

        <div className="partner-sharing-settings">
          <h3>What would you like to share with this partner?</h3>

          <div className="partner-sharing-card">
            <div className="partner-sharing-card-main">
              <div>
                <label>Share Profiles</label>
                <p>
                  Allow this partner to view selected profiles from your
                  organization along with relevant skill insights.
                </p>
              </div>

              {renderPartnerToggle({
                checked: Boolean(partnerState.shareProfiles),
                onChange: (checked) =>
                  setPartnerState((prevPartner) => ({
                    ...prevPartner,
                    shareProfiles: checked,
                    protectProfileIdentity: checked
                      ? prevPartner.protectProfileIdentity
                      : false,
                  })),
              })}
            </div>

            {partnerState.shareProfiles && (
              <div className="partner-sharing-nested-row">
                <div>
                  <label>Protect Profile Identity</label>
                  <p>
                    When enabled, shared profiles appear with a unique profile
                    ID instead of their name and personal details.
                  </p>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                    className="partner-form-helper-link"
                  >
                    Learn more about profile identity protection
                  </a>
                </div>

                {renderPartnerToggle({
                  checked: Boolean(partnerState.protectProfileIdentity),
                  onChange: (checked) =>
                    setPartnerState((prevPartner) => ({
                      ...prevPartner,
                      protectProfileIdentity: checked,
                    })),
                })}
              </div>
            )}
          </div>

          <div className="partner-sharing-card">
            <div className="partner-sharing-card-main">
              <div>
                <label>Share Job Roles</label>
                <p>
                  Allow this partner to view selected job roles from your
                  organization.
                </p>
              </div>

              {renderPartnerToggle({
                checked: Boolean(partnerState.shareJobRoles),
                onChange: (checked) =>
                  setPartnerState((prevPartner) => ({
                    ...prevPartner,
                    shareJobRoles: checked,
                    shareDepartments: checked
                      ? prevPartner.shareDepartments
                      : false,
                  })),
              })}
            </div>

            {partnerState.shareJobRoles && (
              <div className="partner-sharing-nested-row">
                <div>
                  <label>Share Department</label>
                  <p>
                    Used as a job role label/filter. If disabled,
                    department-level details linked to shared job roles will be
                    hidden from the partner.
                  </p>
                </div>

                {renderPartnerToggle({
                  checked: Boolean(partnerState.shareDepartments),
                  onChange: (checked) =>
                    setPartnerState((prevPartner) => ({
                      ...prevPartner,
                      shareDepartments: checked,
                    })),
                })}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const [newDepartment, setNewDepartment] = useState("");
  const [searchDepartment, setSearchDepartment] = useState("");
  const [editingDepartment, setEditingDepartment] = useState({
    index: -1,
    value: "",
  });
  const [showAddDepartmentForm, setShowAddDepartmentForm] = useState(false);

  const [upskillCourseType, setUpskillCourseType] = useState("free");
  const orgAccess = getOrganizationInfoAccess();

  const loggedInRole = getLoggedInUserRole();
  const showDisabledSaveTooltip =
    !orgAccess.canSaveOrgInfo &&
    (loggedInRole === ROLES.MANAGER || loggedInRole === ROLES.GUEST);
  const showDisabledFetchDescriptionTooltip =
    !orgAccess.canFetchDescription &&
    (loggedInRole === ROLES.MANAGER || loggedInRole === ROLES.GUEST);
  const showDisabledAddDepartmentTooltip =
    !orgAccess.canAddDepartment && loggedInRole === ROLES.GUEST;
  const saveDisabledPermissionMessage = t(
    "common.permissions.contactAdminForAction",
  );
  const addDepartmentDisabledPermissionMessage = t(
    "common.permissions.contactAdminforAssistance",
    t("common.permissions.contactAdminForAction"),
  );
  const [businessUnitsCurrentPage, setBusinessUnitsCurrentPage] = useState(1);
  const [businessUnitsPerPage] = useState(5);
  const [businessUnits, setBusinessUnits] = useState([
    {
      id: 1,
      name: "Enterprise Solutions",
      coreSkills: [
        {
          name: "Project Management",
          softSkills: ["Leadership", "Communication", "Problem Solving"],
        },
        {
          name: "Enterprise Architecture",
          softSkills: ["Strategic Thinking", "Technical Communication"],
        },
        {
          name: "Solution Design",
          softSkills: ["Creativity", "Analytical Thinking"],
        },
        {
          name: "Client Management",
          softSkills: ["Relationship Building", "Negotiation"],
        },
      ],
    },
    {
      id: 2,
      name: "Consumer Products",
      coreSkills: [
        {
          name: "Product Development",
          softSkills: ["Innovation", "Collaboration"],
        },
        {
          name: "UX Design",
          softSkills: ["Empathy", "Creative Thinking"],
        },
        {
          name: "Market Research",
          softSkills: ["Analytical Skills", "Attention to Detail"],
        },
        {
          name: "Agile Methodology",
          softSkills: ["Adaptability", "Teamwork"],
        },
      ],
    },
    {
      id: 3,
      name: "Digital Marketing",
      coreSkills: [
        {
          name: "SEO/SEM",
          softSkills: ["Analytical Thinking", "Attention to Detail"],
        },
        {
          name: "Content Strategy",
          softSkills: ["Creativity", "Strategic Planning"],
        },
      ],
    },
    {
      id: 4,
      name: "Data Analytics",
      coreSkills: [
        {
          name: "Statistical Analysis",
          softSkills: ["Critical Thinking", "Problem Solving"],
        },
        {
          name: "Data Visualization",
          softSkills: ["Communication", "Attention to Detail"],
        },
      ],
    },
    {
      id: 5,
      name: "Customer Success",
      coreSkills: [
        {
          name: "Customer Relationship Management",
          softSkills: ["Empathy", "Communication"],
        },
        {
          name: "Account Management",
          softSkills: ["Relationship Building", "Negotiation"],
        },
      ],
    },
    {
      id: 6,
      name: "Quality Assurance",
      coreSkills: [
        {
          name: "Test Planning",
          softSkills: ["Attention to Detail", "Analytical Thinking"],
        },
        {
          name: "Bug Tracking",
          softSkills: ["Communication", "Problem Solving"],
        },
      ],
    },
  ]);
  const [showBusinessUnitForm, setShowBusinessUnitForm] = useState(false);
  const [newBusinessUnit, setNewBusinessUnit] = useState({
    name: "",
    coreSkills: [],
  });
  const [newCoreSkillName, setNewCoreSkillName] = useState("");
  const [newCoreSkillSoftSkills, setNewCoreSkillSoftSkills] = useState([]);
  const [newSoftSkillForCore, setNewSoftSkillForCore] = useState("");
  const [editingBusinessUnit, setEditingBusinessUnit] = useState(null);

  const streamControllerRef = useRef(null);
  const streamRunIdRef = useRef(0);
  const lastChunkRef = useRef("");

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  // Handle organization info form submission
  const handleOrgInfoSubmit = (e) => {
    e.preventDefault();
    setShowModal({
      show: true,
      type: "success",
      title: t("organizationInfo.organizationInfoUpdated"),
      message: t("organizationInfo.infoUpdatedMessage"),
      action: () => {
        setShowModal({
          show: false,
          type: "",
          title: "",
          message: "",
          action: null,
        });
      },
    });
  };

  // Handle editing a department
  const startEditDepartment = (index) => {
    setEditingDepartment({ index, value: departments[index] });
  };

  const saveEditDepartment = () => {
    if (editingDepartment.value.trim()) {
      const updatedDepartments = [...departments];
      updatedDepartments[editingDepartment.index] = editingDepartment.value;
      setDepartments(updatedDepartments);
      setEditingDepartment({ index: -1, value: "" });
    }
  };

  // Handle department selection
  const handleDepartmentSelection = (department) => {
    const isSelected = selectedDepartments.some(
      (selected) => selected._id === department._id,
    );

    if (isSelected) {
      setSelectedDepartments(
        selectedDepartments.filter(
          (selected) => selected._id !== department._id,
        ),
      );
    } else {
      setSelectedDepartments([...selectedDepartments, department]);
    }
  };

  // Handle saving selected departments
  const handleSaveSelectedDepartments = () => {
    setShowModal({
      show: true,
      type: "success",
      title: t("organizationInfo.departmentsSelected"),
      message: t("organizationInfo.selectedDepartmentsSaved", {
        count: selectedDepartments.length,
      }),
      action: () => {
        setShowModal({
          show: false,
          type: "",
          title: "",
          message: "",
          action: null,
        });
      },
    });
  };

  // Filter departments based on search
  const filteredDepartments = departments.filter((dept) =>
    dept.name.toLowerCase().includes(searchDepartment.toLowerCase()),
  );

  // Business units pagination
  const indexOfLastBusinessUnit =
    businessUnitsCurrentPage * businessUnitsPerPage;
  const indexOfFirstBusinessUnit =
    indexOfLastBusinessUnit - businessUnitsPerPage;
  const currentBusinessUnits = businessUnits.slice(
    indexOfFirstBusinessUnit,
    indexOfLastBusinessUnit,
  );
  const totalBusinessUnitsPages = Math.ceil(
    businessUnits.length / businessUnitsPerPage,
  );

  // Handle adding a new business unit
  const handleAddBusinessUnit = () => {
    if (newBusinessUnit.name.trim() && newBusinessUnit.coreSkills.length > 0) {
      if (editingBusinessUnit !== null) {
        // Update existing business unit
        const updatedBusinessUnits = businessUnits.map((unit) =>
          unit.id === editingBusinessUnit
            ? { ...newBusinessUnit, id: editingBusinessUnit }
            : unit,
        );
        setBusinessUnits(updatedBusinessUnits);
        setEditingBusinessUnit(null);
      } else {
        // Add new business unit
        const newId =
          businessUnits.length > 0
            ? Math.max(...businessUnits.map((unit) => unit.id)) + 1
            : 1;
        setBusinessUnits([...businessUnits, { ...newBusinessUnit, id: newId }]);
      }

      // Reset form
      setNewBusinessUnit({ name: "", coreSkills: [] });
      setNewCoreSkillName("");
      setNewCoreSkillSoftSkills([]);
      setNewSoftSkillForCore("");
      setShowBusinessUnitForm(false);
    } else {
      setShowModal({
        show: true,
        type: "warning",
        title: t("organizationInfo.incompleteInformation"),
        message: t("organizationInfo.provideNameAndSkill"),
        action: () => {
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            action: null,
          });
        },
      });
    }
  };

  // Handle editing a business unit
  const handleEditBusinessUnit = (id) => {
    const unitToEdit = businessUnits.find((unit) => unit.id === id);
    if (unitToEdit) {
      setNewBusinessUnit({ ...unitToEdit });
      setEditingBusinessUnit(id);
      setShowBusinessUnitForm(true);
    }
  };

  // Handle deleting a business unit
  const handleDeleteBusinessUnit = (id) => {
    setShowModal({
      show: true,
      type: "warning",
      title: t("organizationInfo.deleteBusinessUnit"),
      message: t("organizationInfo.confirmDeleteBusinessUnit"),
      action: () => {
        setBusinessUnits(businessUnits.filter((unit) => unit.id !== id));
        setShowModal({
          show: false,
          type: "",
          title: "",
          message: "",
          action: null,
        });
      },
    });
  };

  // Handle adding a core skill
  const handleAddCoreSkill = () => {
    if (newCoreSkillName.trim()) {
      const newCoreSkill = {
        name: newCoreSkillName.trim(),
        softSkills: [],
      };
      setNewBusinessUnit({
        ...newBusinessUnit,
        coreSkills: [...newBusinessUnit.coreSkills, newCoreSkill],
      });
      setNewCoreSkillName("");
      setNewCoreSkillSoftSkills([]);
    }
  };

  // Handle adding a soft skill to a core skill
  const handleAddSoftSkillToCore = (coreSkillIndex) => {
    if (newSoftSkillForCore.trim()) {
      const updatedCoreSkills = [...newBusinessUnit.coreSkills];
      updatedCoreSkills[coreSkillIndex].softSkills = [
        ...updatedCoreSkills[coreSkillIndex].softSkills,
        newSoftSkillForCore.trim(),
      ];
      setNewBusinessUnit({
        ...newBusinessUnit,
        coreSkills: updatedCoreSkills,
      });
      setNewSoftSkillForCore("");
    }
  };

  // Handle removing a core skill
  const handleRemoveCoreSkill = (index) => {
    const updatedCoreSkills = [...newBusinessUnit.coreSkills];
    updatedCoreSkills.splice(index, 1);
    setNewBusinessUnit({
      ...newBusinessUnit,
      coreSkills: updatedCoreSkills,
    });
  };

  // Handle removing a soft skill from a core skill
  const handleRemoveSoftSkillFromCore = (coreSkillIndex, softSkillIndex) => {
    const updatedCoreSkills = [...newBusinessUnit.coreSkills];
    updatedCoreSkills[coreSkillIndex].softSkills.splice(softSkillIndex, 1);
    setNewBusinessUnit({
      ...newBusinessUnit,
      coreSkills: updatedCoreSkills,
    });
  };

  const handleDeleteDepartment = async (department) => {
    setShowModal({
      show: true,
      showButtons: true,
      type: "warning",
      title: t("common.deleteDepartmentTitle"),
      message: t("common.deleteDepartmentMessage", {
        name: department.name,
      }),
      children: <div actionLabel={t("organizationInfo.delete")} />,
      action: async () => {
        const response = await deleteDepartmentApi(department._id);

        if (response.code === 200) {
          const updatedDepartments = await getDepartmentsListApiHandler();
          setShowModal({
            show: true,
            type: "success",
            title: t("common.success"),
            message: t("common.deleteDepartmentSuccess", {
              name: department.name,
            }),
            onConfirm: null,
          });

          setTimeout(() => {
            getDepartmentsMasterListApiHandler(updatedDepartments);
          }, 500);
        }
      },
    });
  };

  const [orgInfos, setOrgInfos] = useState({
    org_name: "",
    industry: "",
    organizationCode: "",
    website: "",
    country: "",
    description: "",
  });
  const [showOrganizationCode, setShowOrganizationCode] = useState(false);
  const [savedIndustry, setSavedIndustry] = useState("");
  const [showCustomIndustryInput, setShowCustomIndustryInput] = useState(false);

  useEffect(() => {
    getDepartmentsListApiHandler();
    getOrgInfoHandler();
    getIndustriesListApiHandler();
    getCountriesListApiHandler();
  }, []);

  const setShowAddDepartmentFormHandler = () => {
    setShowAddDepartmentForm(true);
    getDepartmentsMasterListApiHandler();
  };

  const getDepartmentsMasterListApiHandler = async (
    currentDepartments = departments,
  ) => {
    try {
      const response = await getDepartmentsMasterListApi();

      const suggestions = response.data.filter(
        (dept) => !currentDepartments.includes(dept),
      );

      setDepartmentSuggestions(suggestions);
    } catch (error) {
      handleApiError(error, { showModal: false });
    }
  };

  const getOrgInfoHandler = async () => {
    try {
      const response = await getOrgInfoApi();
      const organizationData = response?.data || {};

      const fetchedIndustry = organizationData.industry || "";

      const isPartnerCollaborationEnabled = normalizeBoolean(
        organizationData.partnerCollaboration,
      );

      setOrgInfos({
        org_name: organizationData.organization || "",
        industry: fetchedIndustry,
        organizationCode: organizationData.orgCode || "",
        website: organizationData.website || "",
        country: organizationData.country || "",
        description: organizationData.description || "",
        partnerCollaboration: isPartnerCollaborationEnabled,
      });

      /*
       * Synchronize the API value with the shared collaboration state.
       * This updates this page and all other subscribed components.
       */
      const updatedCollaborationState = writePartnerCollaborationState({
        enabled: isPartnerCollaborationEnabled,
      });

      setPartnerCollaboration(updatedCollaborationState);

      setSavedIndustry(fetchedIndustry);

      if (organizationData.upskillCourseType) {
        setUpskillCourseType(organizationData.upskillCourseType);
      }

      console.log("Organization info fetched successfully:", organizationData);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  const normalizePartnerStatus = (value) => {
    const status = String(value || "")
      .trim()
      .toUpperCase();

    if (["ACTIVE", "ACCEPTED", "CONNECTED"].includes(status)) {
      return "Active";
    }

    if (["PENDING", "INVITED", "WAITING"].includes(status)) {
      return "Pending";
    }

    if (
      [
        "INACTIVE",
        "DECLINED",
        "REJECTED",
        "REMOVED",
        "DISCONNECTED",
        "EXPIRED",
      ].includes(status)
    ) {
      return "Inactive";
    }

    if (!status) return "Unknown";

    return `${status.charAt(0)}${status.slice(1).toLowerCase()}`;
  };

  const getCurrentTenantId = () =>
    sessionStorage.getItem("tenantId") ||
    localStorage.getItem("tenantId") ||
    "";

  const normalizePartnerDirection = (partner, normalizedStatus = "") => {
    const invitation = String(partner?.invitation || "")
      .trim()
      .toUpperCase();

    const direction = String(partner?.direction || "")
      .trim()
      .toUpperCase();

    if (invitation === "RECEIVED" || direction === "RECEIVED") {
      return "received";
    }

    if (invitation === "SENT" || direction === "SENT") {
      return "sent";
    }

    if (
      invitation === "ACCEPTED" ||
      direction === "ACCEPTED" ||
      normalizedStatus === "Active"
    ) {
      return "accepted";
    }

    return "";
  };

  const getJobRoleId = (jobRole) => {
    if (!jobRole) return "";

    if (typeof jobRole === "string") {
      return jobRole;
    }

    return (
      jobRole.jobId || jobRole.jobRoleId || jobRole._id || jobRole.id || ""
    );
  };

  const getDepartmentValue = (department) => {
    if (!department) return "";

    if (typeof department === "string") {
      return department;
    }

    return department._id || department.id || department.name || "";
  };

  const getOptionalUnreadValue = (partner) => {
    const unreadValue =
      partner.isUnread ?? partner.unread ?? partner.invitationUnread;

    /*
     * Returning undefined lets the existing global unread
     * state act as a fallback.
     */
    if (unreadValue === undefined || unreadValue === null) {
      return undefined;
    }

    return normalizeBoolean(unreadValue);
  };
  const getPartnerListQueryParams = () => {
    const queryParams = {
      search: partnerDirectorySearch.trim(),
      status:
        partnerStatusFilter === "All"
          ? ""
          : partnerStatusFilter.trim(),
      invitation:
        partnerDirectionFilter === "All"
          ? ""
          : partnerDirectionFilter.trim(),
      sharedData:
        partnerSharedDataFilter === "All" ? "" : partnerSharedDataFilter.trim(),
      pageNo: partnerCurrentPage,
      pageSize: partnerItemsPerPage,
    };

    /* Do not send unchecked/empty optional filters to the backend. */
    return Object.fromEntries(
      Object.entries(queryParams).filter(
        ([, value]) => value !== "" && value !== null && value !== undefined,
      ),
    );
  };

  const extractPartnerListResponse = (response) => {
    const payload = response?.data ?? response ?? {};

    const apiPartners = Array.isArray(payload)
      ? payload
      : [
          payload?.partners,
          payload?.items,
          payload?.content,
          payload?.records,
          payload?.results,
          payload?.data,
          response?.partners,
        ].find(Array.isArray) || [];

    const rawTotalCount =
      payload?.totalCount ??
      payload?.totalElements ??
      payload?.totalRecords ??
      payload?.count ??
      payload?.pagination?.totalCount ??
      payload?.pagination?.totalElements ??
      payload?.pagination?.totalRecords ??
      payload?.page?.totalElements ??
      response?.totalCount ??
      response?.totalElements ??
      response?.totalRecords;

    const parsedTotalCount = Number(rawTotalCount);

    return {
      apiPartners,
      totalCount: Number.isFinite(parsedTotalCount)
        ? parsedTotalCount
        : apiPartners.length,
    };
  };

  const fetchPartnersListApi = async () => {
    setIsPartnersLoading(true);

    try {
      const queryParams = getPartnerListQueryParams();
      const response = await getPartnersListApi(queryParams);
      const { apiPartners, totalCount } = extractPartnerListResponse(response);

      const modifiedResponse = apiPartners
        .map((partner) => {
          const normalizedStatus = normalizePartnerStatus(
            partner.status || partner.connectionStatus,
          );

          const normalizedDirection = normalizePartnerDirection(
            partner,
            normalizedStatus,
          );

          const sharedData = Array.isArray(partner.sharedData)
            ? partner.sharedData
                .map((item) => String(item || "").trim())
                .filter(Boolean)
            : [];

          const normalizedSharedData = sharedData.map((item) =>
            item.toLowerCase(),
          );

          const hasProfiles = normalizedSharedData.includes("profiles");
          const hasJobRoles = normalizedSharedData.includes("job roles");
          const hasDepartments = normalizedSharedData.includes("departments");

          return {
            ...partner,

            id: partner._id || partner.id || partner.partnerId || "",
            _id: partner._id || partner.id || partner.partnerId || "",

            name:
              partner.partnerOrganization ||
              partner.organizationName ||
              partner.name ||
              "-",

            organizationCode:
              partner.partnerOrgCode ||
              partner.organizationCode ||
              partner.orgCode ||
              "-",

            contactPerson:
              partner.partnerSuperAdminName ||
              partner.contactPerson ||
              partner.adminName ||
              "-",

            contactEmail:
              partner.partnerSuperAdminEmail ||
              partner.contactEmail ||
              partner.adminEmail ||
              "-",

            rawStatus: String(
              partner.status || partner.connectionStatus || "",
            )
              .trim()
              .toUpperCase(),

            status: normalizedStatus,

            invitation: String(partner.invitation || "")
              .trim()
              .toUpperCase(),

            direction: normalizedDirection,

            sharedData,

            shareProfiles:
              hasProfiles ||
              normalizeBoolean(partner.profileShare ?? partner.shareProfiles),

            shareJobRoles:
              hasJobRoles ||
              normalizeBoolean(partner.jobRolesShare ?? partner.shareJobRoles),

            shareDepartments:
              hasDepartments ||
              normalizeBoolean(
                partner.departmentShare ?? partner.shareDepartments,
              ),

            protectProfileIdentity: normalizeBoolean(
              partner.protectProfileIdentity,
            ),

            dateSent:
              partner.dateSent ||
              partner.invitedDate ||
              partner.invitedAt ||
              partner.createdDate ||
              partner.createdAt ||
              "",

            expiredAt:
              partner.expiredAt ||
              partner.expiresAt ||
              partner.invitationExpiresAt ||
              "",

            lastUpdated:
              partner.updatedDate ||
              partner.updatedAt ||
              partner.createdDate ||
              partner.createdAt ||
              "",

            sharedProfileIds: Array.isArray(partner.profiles)
              ? partner.profiles.map(getProfileUserId).filter(Boolean)
              : [],

            sharedJobRoleIds: Array.isArray(partner.jobRoles)
              ? partner.jobRoles.map(getJobRoleId).filter(Boolean)
              : [],

            sharedDepartments: Array.isArray(partner.departments)
              ? partner.departments.map(getDepartmentValue).filter(Boolean)
              : [],

            inactiveReason:
              partner.inactiveReason ||
              partner.declineReason ||
              partner.removedReason ||
              "",

            isUnread: getOptionalUnreadValue(partner),
          };
        })
        .filter((partner) => Boolean(partner.id));

      console.log("Partner directory query:", queryParams);
      console.log("Normalized partner directory:", modifiedResponse);

      setPartners(modifiedResponse);
      setPartnerTotalCount(totalCount);

      const hasPendingReceivedOnCurrentPage = modifiedResponse.some((partner) => {
        const status = String(partner?.status || "")
          .trim()
          .toUpperCase();

        const invitation = String(
          partner?.invitation ||
            partner?.direction ||
            "",
        )
          .trim()
          .toUpperCase();

        return status === "PENDING" && invitation === "RECEIVED";
      });

      if (hasPendingReceivedOnCurrentPage) {
        /*
         * We already KNOW a pending partner exists from the directory
         * response, so update the entire notification trail immediately.
         */
        setPartnerInviteUnread(true);
      } else {
        /*
         * Current page may be filtered/paginated and therefore cannot
         * prove that there are no pending partners globally.
         */
        await syncPendingPartnerInviteUnread();
      }
    } catch (error) {
      console.error("Error fetching partner directory:", error);

      setPartners([]);
      setPartnerTotalCount(0);
      handleApiError(error, { showModal: false });
    } finally {
      setIsPartnersLoading(false);
    }
  };

  useEffect(() => {
    // Disabling collaboration clears the directory. Re-fetch it whenever the
    // feature changes back from disabled to enabled.
    if (!partnerCollaboration.enabled) {
      setPartnerInviteUnread(false);
      return;
    }

    fetchPartnersListApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    partnerCollaboration.enabled,
    partnerCurrentPage,
    partnerItemsPerPage,
    partnerDirectorySearch,
    partnerStatusFilter,
    partnerDirectionFilter,
    partnerSharedDataFilter,
  ]);

  const handleFetchOrganizationDescription = async () => {
    setIsLoadingOrgInfo(true);

    setOrgInfos((prev) => ({ ...prev, description: "" }));

    if (streamControllerRef.current) {
      try {
        streamControllerRef.current.abort();
      } catch {}
    }

    const controller = new AbortController();
    streamControllerRef.current = controller;
    const myRunId = ++streamRunIdRef.current;

    try {
      const payload = {
        url: orgInfos.website,
        name: orgInfos.org_name,
      };

      await generateCompanyInfoStream(
        payload,
        (chunk) => {
          if (myRunId !== streamRunIdRef.current) return;
          if (!chunk) return;

          setOrgInfos((prev) => ({
            ...prev,
            description: (prev.description || "") + String(chunk),
          }));
        },
        {
          signal: controller.signal,
          delayMs: 0,
          jsonField: null,
        },
      );
    } catch (error) {
      if (error?.name !== "AbortError") {
        handleApiError(error, { showModal: true });
      }
    } finally {
      if (myRunId === streamRunIdRef.current) {
        streamControllerRef.current = null;
        setIsLoadingOrgInfo(false);
      }
    }
  };

  const [industries, setIndustries] = useState(null);
  const getIndustriesListApiHandler = async () => {
    try {
      const response = await getIndustriesListApi();
      setIndustries(response.data);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const [countries, setCountries] = useState(null);
  const getCountriesListApiHandler = async () => {
    try {
      const response = await getCountriesListApi();
      setCountries(response.data);
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  const [isLoadingOrgInfo, setIsLoadingOrgInfo] = useState(false);

  const handleOrgInfoFormChange = (e) => {
    const { id, value } = e.target;

    setOrgInfos((prev) => ({
      ...prev,
      [id]: value,
    }));

    console.log("Organization info updated:", {
      ...orgInfos,
      [id]: value,
    });
  };

  const handleShowIndustryDropdown = () => {
    setShowCustomIndustryInput(false);
    setOrgInfos((prev) => ({
      ...prev,
      industry: savedIndustry,
    }));
  };

  const handleSaveOrgDetails = async (e) => {
    e.preventDefault();
    try {
      const obj = {
        orgName: orgInfos.org_name,
        description: orgInfos.description,
        industry: orgInfos.industry,
        website: orgInfos.website,
        country: orgInfos.country,
        upskillCourseType: upskillCourseType,
        partnerCollaboration: Boolean(partnerCollaboration.enabled),
      };
      const response = await saveOrgInfoApi(obj);
      console.log("Organization details saved successfully:", response.data);
      if (response.code === 200) {
        MessageManager.showToast(
          "success",
          t("organizationInfo.orgDetailsSaved"),
        );

        await getOrgInfoHandler();
        setShowCustomIndustryInput(false);
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
      });
    }
  };

  const industryValue = orgInfos?.industry ?? "";
  const industryOptions = Array.isArray(industries) ? industries : [];
  const shouldShowSavedIndustryOption =
    !showCustomIndustryInput &&
    industryValue &&
    !industryOptions.includes(industryValue) &&
    !isOtherIndustryOption(industryValue);

  const getDepartmentsListApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      console.log("Departments list fetched successfully:", response.data);
      const updatedList = response.data || [];
      setDepartments(updatedList);
      return updatedList;
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
      return [];
    }
  };
  const handleCancelInvitation = async (partnerId) => {
    try {
      const obj = {
        id: partnerId,
      };
      console.log("Cancelling partner invitation with payload:", obj);
      const response = await cancelInvitationApi(obj);
      setPartnerManageModalOpen(false);
      setPartnerActionMode("");
      setManagingPartner(null);
      fetchPartnersListApi();
      MessageManager.showToast(
        "success",
        "Partner invite cancelled",
      );

      console.log("Partner invitation cancelled successfully:", response);
    } catch (error) {
      handleApiError(error, { showModal: true });
    }
  };
  // Handle adding a department suggestion
  const handleAddSuggestion = async (arg) => {
    try {
      // If called via the "Add" button, the first arg is a click event.
      // Prevent default and ignore it as a suggestion value.
      if (arg && typeof arg !== "string") {
        arg.preventDefault?.();
        arg.stopPropagation?.();
        arg = undefined;
      }

      const inputValue = (newDepartment || "").trim();
      let departmentsToAdd = [];

      if (inputValue) {
        departmentsToAdd = inputValue
          .split(",")
          .map((d) => d.trim())
          .filter(Boolean);
      } else if (typeof arg === "string" && arg.trim()) {
        // Coming from a suggestion pill
        departmentsToAdd = [arg.trim()];
      } else {
        // Nothing to add
        return;
      }

      const response = await addDepartmentApi(departmentsToAdd);

      if (response?.code === 200) {
        await getDepartmentsListApiHandler();

        // Clear the input
        setNewDepartment("");

        MessageManager.showToast(
          "success",
          t("organizationInfo.departmentAdded", { name: departmentsToAdd[0] }),
        );

        // If we added a suggestion, remove it from the suggestions list
        if (departmentsToAdd.length === 1 && typeof arg === "string") {
          setDepartmentSuggestions((prev) => prev.filter((d) => d !== arg));
        }
      }
    } catch (error) {
      handleApiError(error, { showModal: true });
    }
  };

  return (
    <div className="org-info-page">
      <BackButton />

      <main className="org-info-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <Building size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              {/* <h1>{t("organizationInfo.pageTitle")}</h1> */}
              <h1>{t("organizationInfo.pageTitleOrganization")}</h1>

              <span className="org-info-title-info-wrap">
                <button
                  type="button"
                  className="org-info-title-info-btn"
                  aria-label={t("organizationInfo.tooltips.title")}
                >
                  <Info size={18} />
                </button>

                <span className="org-info-title-info-popover" role="tooltip">
                  <strong>{t("organizationInfo.tooltips.title")}</strong>
                  <span>{t("organizationInfo.tooltips.message")}</span>
                </span>
              </span>
            </div>
            <p>{t("organizationInfo.pageDescription")}</p>
          </div>

          <div className="org-tab-navigation">
            <button
              className={`org-tab-btn ${activeTab === "info" ? "active" : ""}`}
              onClick={() => setActiveTab("info")}
            >
              {t("organizationInfo.organizationInfo")}
            </button>
            <button
              className={`org-tab-btn ${
                activeTab === "departments" ? "active" : ""
              }`}
              onClick={() => setActiveTab("departments")}
            >
              {t("organizationInfo.departments")}
            </button>
            <button
              className={`org-tab-btn partner-tab-notification-anchor ${
                activeTab === "hiring-partners" ? "active" : ""
              }`}
              onClick={() => setActiveTab("hiring-partners")}
            >
              Partners
              {hasUnreadPartnerInvite && (
                <span
                  className="partner-notification-dot"
                  aria-label="Pending partner invitation"
                />
              )}
            </button>
            {/* <button
              className={`org-tab-btn ${activeTab === "business-units" ? "active" : ""}`}
              onClick={() => setActiveTab("business-units")}
            >
              {t("organizationInfo.businessUnits")}
            </button> */}
          </div>

          {/* Organization Info Tab */}
          {activeTab === "info" && (
            <div className="tab-content">
              <form className="org-info-form">
                <div className="form-group">
                  <div className="org-name-label-row">
                    <label htmlFor="orgName">
                      {t("organizationInfo.organizationName")}
                    </label>
                    <CustomTooltip
                      tooltip={{
                        title: "",
                        message: t(
                          "organizationInfo.organizationNameChangeLockedTooltip",
                        ),
                      }}
                      id="org-name-lock-tooltip"
                      className="org-name-lock-tooltip"
                    >
                      <Info
                        size={14}
                        color="#888"
                        style={{
                          cursor: "pointer",
                          verticalAlign: "middle",
                        }}
                      />
                    </CustomTooltip>
                  </div>
                  <input
                    disabled
                    type="text"
                    id="org_name"
                    value={orgInfos?.org_name || ""}
                    onChange={handleOrgInfoFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="industry">
                    {t("organizationInfo.industry")}
                  </label>
                  {showCustomIndustryInput ? (
                    <div className="industry-input-wrapper">
                      <input
                        disabled={!orgAccess.canEditOrgInfo}
                        type="text"
                        id="industry"
                        value={industryValue}
                        onChange={handleOrgInfoFormChange}
                        placeholder={t("organizationInfo.enterIndustry")}
                        required
                        className={
                          !orgAccess.canEditOrgInfo
                            ? "org-permission-disabled"
                            : ""
                        }
                      />
                      <button
                        type="button"
                        className="industry-reset-btn"
                        onClick={handleShowIndustryDropdown}
                        aria-label={t("organizationInfo.showIndustryDropdown")}
                        title={t("organizationInfo.showIndustryDropdown")}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <select
                      id="industry"
                      value={industryValue}
                      onChange={handleOrgInfoFormChange}
                      required
                      disabled={!orgAccess.canEditOrgInfo}
                      className={
                        !orgAccess.canEditOrgInfo
                          ? "org-permission-disabled"
                          : ""
                      }
                    >
                      <option value="" disabled hidden>
                        {t("organizationInfo.selectIndustry")}
                      </option>
                      {shouldShowSavedIndustryOption && (
                        <option value={industryValue}>{industryValue}</option>
                      )}
                      {industryOptions.map((industry, i) => (
                        <option key={i} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="organizationCode">Organization Code</label>
                  <div className="organization-code-field">
                    <input
                      type={showOrganizationCode ? "text" : "password"}
                      id="organizationCode"
                      value={orgInfos?.organizationCode}
                      readOnly
                    />
                    <button
                      type="button"
                      className="mmh-btn mmh-btn-secondary organization-code-toggle-btn"
                      onClick={() =>
                        setShowOrganizationCode((current) => !current)
                      }
                      title={
                        showOrganizationCode
                          ? "Hide organization code"
                          : "Show organization code"
                      }
                      aria-label={
                        showOrganizationCode
                          ? "Hide organization code"
                          : "Show organization code"
                      }
                    >
                      {showOrganizationCode ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="webAddress">
                    {t("organizationInfo.webAddress")}
                  </label>
                  <input
                    type="url"
                    id="website"
                    value={orgInfos?.website || ""}
                    onChange={handleOrgInfoFormChange}
                    placeholder={t("organizationInfo.websitePlaceholder")}
                    disabled={!orgAccess.canEditOrgInfo}
                    className={
                      !orgAccess.canEditOrgInfo ? "org-permission-disabled" : ""
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">
                    {t("organizationInfo.country")}
                  </label>
                  <select
                    id="country"
                    value={orgInfos?.country ?? ""}
                    onChange={handleOrgInfoFormChange}
                    disabled={!orgAccess.canEditOrgInfo}
                    required
                    className={
                      !orgAccess.canEditOrgInfo ? "org-permission-disabled" : ""
                    }
                  >
                    <option value="" disabled hidden>
                      {t("organizationInfo.selectCountry")}
                    </option>
                    {Array.isArray(countries) &&
                      countries.map((country, i) => (
                        <option key={i} value={country}>
                          {country}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-group full-width">
                  <div className="org-about-heading-row">
                    <label htmlFor="about">
                      {t("organizationInfo.aboutYourOrganization")}
                      <CustomTooltip
                        tooltip={{
                          title: t(
                            "organizationInfo.tooltips.aboutYourOrganizationTitle",
                          ),
                          message: t(
                            "organizationInfo.tooltips.aboutYourOrganizationMessage",
                          ),
                        }}
                        id="org-info-about-tooltip"
                      >
                        <Info
                          size={14}
                          color="#888"
                          style={{
                            marginLeft: "8px",
                            cursor: "pointer",
                            verticalAlign: "middle",
                          }}
                        />
                      </CustomTooltip>
                    </label>
                    <button
                      type="button"
                      className="btn btn-primary fetch-description-btn"
                      onClick={handleFetchOrganizationDescription}
                      disabled={
                        !orgAccess.canFetchDescription || isLoadingOrgInfo
                      }
                      title={
                        showDisabledFetchDescriptionTooltip
                          ? saveDisabledPermissionMessage
                          : ""
                      }
                    >
                      {isLoadingOrgInfo ? (
                        <>
                          <div className="onboarding-wizard-btn-spinner"></div>
                          {t("onboarding.fetching")}
                        </>
                      ) : (
                        <>
                          {t("onboarding.fetchDescription")}
                          <Sparkles size={16} />
                        </>
                      )}
                    </button>
                  </div>
                  <div className="description-section">
                    <textarea
                      id="description"
                      value={orgInfos?.description || ""}
                      onChange={handleOrgInfoFormChange}
                      rows={5}
                      placeholder={t("organizationInfo.describeOrganization")}
                      disabled={!orgAccess.canEditOrgInfo}
                      className={
                        !orgAccess.canEditOrgInfo
                          ? "org-permission-disabled"
                          : ""
                      }
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>
                    {t("organizationInfo.professionalDevelopmentModel")}
                  </label>
                  <div className="upskilling-toggle-container">
                    <button
                      disabled={!orgAccess.canEditProfessionalDevelopmentModel}
                      type="button"
                      className={`upskilling-toggle-option ${
                        upskillCourseType === "free" ? "active" : ""
                      }${!orgAccess.canEditProfessionalDevelopmentModel ? " org-permission-disabled" : ""}`}
                      onClick={() => setUpskillCourseType("free")}
                    >
                      <span className="upskilling-option-label">
                        {t("organizationInfo.freeCourses")}
                        <CustomTooltip
                          tooltip={{
                            title: t(
                              "organizationInfo.tooltips.freeCoursesTitle",
                            ),
                            message: t(
                              "organizationInfo.tooltips.freeCoursesMessage",
                            ),
                          }}
                          id="free-courses-tooltip"
                          className="free-courses-tooltip-compact"
                        >
                          <Info
                            size={14}
                            color="#888"
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              verticalAlign: "middle",
                            }}
                          />
                        </CustomTooltip>
                      </span>
                      <span className="upskilling-option-desc">
                        {t("organizationInfo.freeCoursesDescription")}
                      </span>
                    </button>
                    <button
                      disabled={!orgAccess.canEditProfessionalDevelopmentModel}
                      type="button"
                      className={`upskilling-toggle-option ${
                        upskillCourseType === "paid" ? "active" : ""
                      }${!orgAccess.canEditProfessionalDevelopmentModel ? " org-permission-disabled" : ""}`}
                      onClick={() => setUpskillCourseType("paid")}
                    >
                      <span className="upskilling-option-label">
                        {t("organizationInfo.premiumCourses")}
                        <CustomTooltip
                          tooltip={{
                            title: t(
                              "organizationInfo.tooltips.premiumCoursesTitle",
                            ),
                            message: t(
                              "organizationInfo.tooltips.premiumCoursesMessage",
                            ),
                          }}
                          id="premium-courses-tooltip"
                        >
                          <Info
                            size={14}
                            color="#888"
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              verticalAlign: "middle",
                            }}
                          />
                        </CustomTooltip>
                      </span>
                      <span className="upskilling-option-desc">
                        {t("organizationInfo.premiumCoursesDescription")}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    disabled={!orgAccess.canSaveOrgInfo}
                    type="submit"
                    className="btn btn-primary"
                    onClick={handleSaveOrgDetails}
                    title={
                      showDisabledSaveTooltip
                        ? saveDisabledPermissionMessage
                        : ""
                    }
                  >
                    <Save size={16} />
                    {t("organizationInfo.saveChanges")}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === "departments" && (
            <div className="tab-content">
              <div className="departments-section">
                <div className="section-header departments-header-row">
                  <div className="departments-header-copy">
                    <h2>{t("organizationInfo.manageDepartments")}</h2>
                    <p>{t("organizationInfo.manageDepartmentsSubtitle")}</p>
                  </div>

                  {!showAddDepartmentForm && (
                    <button
                      disabled={!orgAccess.canAddDepartment}
                      className="btn btn-primary"
                      onClick={() => setShowAddDepartmentFormHandler()}
                      title={
                        showDisabledAddDepartmentTooltip
                          ? addDepartmentDisabledPermissionMessage
                          : ""
                      }
                    >
                      <Plus size={16} />
                      {t("organizationInfo.addDepartment")}
                    </button>
                  )}
                </div>

                {/* ADDING DEPARTMENT FORM */}
                <div className="add-department-section">
                  {showAddDepartmentForm && (
                    <div className="add-department-form">
                      <div className="input-group">
                        <input
                          type="text"
                          id="newDepartmentInput"
                          placeholder={t(
                            "organizationInfo.addDepartmentInputPlaceholder",
                          )}
                          value={newDepartment}
                          onChange={(e) => setNewDepartment(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddSuggestion();
                            }
                          }}
                        />

                        <button
                          className="btn btn-primary"
                          onClick={handleAddSuggestion}
                          disabled={!newDepartment.trim()}
                        >
                          <Plus size={16} />
                          {t("common.add")}
                        </button>
                        <button
                          className="btn btn-outline"
                          onClick={() => {
                            setShowAddDepartmentForm(false);
                            setNewDepartment("");
                          }}
                        >
                          {t("common.cancel")}
                        </button>
                      </div>

                      <div className="department-suggestions">
                        <h3
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          {t("organizationInfo.suggestedDepartments")}
                          <CustomTooltip
                            tooltip={{
                              title: t(
                                "organizationInfo.tooltips.selectDepartmentsTitle",
                              ),
                              message: t(
                                "organizationInfo.tooltips.selectDepartmentsMessage",
                              ),
                            }}
                            id="suggested-departments-tooltip"
                          >
                            <Info
                              size={14}
                              color="#888"
                              style={{
                                marginLeft: "8px",
                                cursor: "pointer",
                                verticalAlign: "middle",
                              }}
                            />
                          </CustomTooltip>
                        </h3>

                        <div className="suggestion-pills">
                          {departmentSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              className="suggestion-pill"
                              onClick={() => handleAddSuggestion(suggestion)}
                            >
                              <Plus size={12} />
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="departments-list-section">
                  <div className="search-container">
                    <Search size={18} className="search-icon" />
                    <input
                      type="text"
                      placeholder={t("organizationInfo.searchDepartments")}
                      value={searchDepartment}
                      onChange={(e) => setSearchDepartment(e.target.value)}
                      className="search-input"
                    />
                  </div>

                  <div className="departments-list">
                    {filteredDepartments.length > 0 ? (
                      filteredDepartments.map((department, index) => (
                        <div key={index} className="department-item">
                          {editingDepartment.index === index ? (
                            <div className="edit-department"></div>
                          ) : (
                            <>
                              <span className="department-name">
                                {department.name}
                              </span>
                              {orgAccess.canDeleteDepartment && (
                                <div className="department-actions">
                                  <button
                                    className="action-btn"
                                    onClick={() =>
                                      handleDeleteDepartment(department)
                                    }
                                    title={t(
                                      "organizationInfo.deleteDepartmentTooltip",
                                    )}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="no-results">
                        {t("organizationInfo.noDepartmentsFound")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "business-units" && (
            <div className="tab-content">
              <div className="business-units-section">
                <div className="section-header">
                  <h2>{t("organizationInfo.businessUnits")}</h2>
                  <p>{t("organizationInfo.manageBusinessUnits")}</p>
                </div>

                <div className="add-business-unit">
                  {!showBusinessUnitForm ? (
                    <button
                      className="btn btn-primary add-unit-btn"
                      onClick={() => setShowBusinessUnitForm(true)}
                    >
                      <Plus size={16} />
                      {t("organizationInfo.addBusinessUnit")}
                    </button>
                  ) : (
                    <div className="business-unit-form">
                      <h3>
                        {editingBusinessUnit !== null
                          ? t("organizationInfo.editBusinessUnit")
                          : t("organizationInfo.addBusinessUnit")}
                      </h3>

                      <div className="form-group">
                        <label>{t("organizationInfo.businessUnitName")}</label>
                        <input
                          type="text"
                          placeholder={t(
                            "organizationInfo.enterBusinessUnitName",
                          )}
                          value={newBusinessUnit.name}
                          onChange={(e) =>
                            setNewBusinessUnit({
                              ...newBusinessUnit,
                              name: e.target.value,
                            })
                          }
                          required
                        />
                      </div>

                      <div className="core-skills-section">
                        <h4>{t("organizationInfo.coreSkills")}</h4>
                        {newBusinessUnit.coreSkills.map(
                          (coreSkill, coreSkillIndex) => (
                            <div
                              key={coreSkillIndex}
                              className="core-skill-item"
                            >
                              <div className="core-skill-header">
                                <span>{coreSkill.name}</span>
                                <button
                                  className="remove-skill"
                                  onClick={() =>
                                    handleRemoveCoreSkill(coreSkillIndex)
                                  }
                                >
                                  <X size={12} />
                                </button>
                              </div>
                              <div className="soft-skills-section">
                                <h5>{t("organizationInfo.softSkills")}</h5>
                                {coreSkill.softSkills.map(
                                  (softSkill, softSkillIndex) => (
                                    <span
                                      key={softSkillIndex}
                                      className="skill-tag soft"
                                    >
                                      {softSkill}
                                      <button
                                        className="remove-skill"
                                        onClick={() =>
                                          handleRemoveSoftSkillFromCore(
                                            coreSkillIndex,
                                            softSkillIndex,
                                          )
                                        }
                                      >
                                        <X size={12} />
                                      </button>
                                    </span>
                                  ),
                                )}
                                <div className="skill-input-group">
                                  <input
                                    type="text"
                                    placeholder={t(
                                      "organizationInfo.addSoftSkill",
                                    )}
                                    value={newSoftSkillForCore}
                                    onChange={(e) =>
                                      setNewSoftSkillForCore(e.target.value)
                                    }
                                    onKeyPress={(e) =>
                                      e.key === "Enter" &&
                                      (e.preventDefault(),
                                      handleAddSoftSkillToCore(coreSkillIndex))
                                    }
                                  />
                                  <button
                                    className="btn btn-sm"
                                    onClick={() =>
                                      handleAddSoftSkillToCore(coreSkillIndex)
                                    }
                                  >
                                    <Plus size={14} />
                                    {t("common.add")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                        <div className="add-core-skill">
                          <input
                            type="text"
                            placeholder={t("organizationInfo.newCoreSkillName")}
                            value={newCoreSkillName}
                            onChange={(e) =>
                              setNewCoreSkillName(e.target.value)
                            }
                            onKeyPress={(e) =>
                              e.key === "Enter" &&
                              (e.preventDefault(), handleAddCoreSkill())
                            }
                          />
                          <button
                            className="btn btn-sm"
                            onClick={handleAddCoreSkill}
                          >
                            <Plus size={14} />
                            {t("organizationInfo.addCoreSkill")}
                          </button>
                        </div>
                      </div>

                      <div className="form-actions">
                        <button
                          className="btn btn-outline"
                          onClick={() => {
                            setShowBusinessUnitForm(false);
                            setNewBusinessUnit({ name: "", coreSkills: [] });
                            setEditingBusinessUnit(null);
                          }}
                        >
                          {t("common.cancel")}
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleAddBusinessUnit}
                        >
                          {editingBusinessUnit !== null
                            ? t("common.update")
                            : t("common.add")}{" "}
                          {t("organizationInfo.businessUnit")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="business-units-nested-table">
                  <div className="table-header">
                    <div className="header-cell unit-name">
                      {t("organizationInfo.businessUnit")}
                    </div>
                    <div className="header-cell core-skills">
                      {t("organizationInfo.coreSkills")}
                    </div>
                    <div className="header-cell soft-skills">
                      {t("organizationInfo.softSkills")}
                    </div>
                    <div className="header-cell actions">
                      {t("common.actions")}
                    </div>
                  </div>

                  {currentBusinessUnits.map((unit) => (
                    <div key={unit.id} className="unit-row">
                      <div
                        className="unit-name-cell"
                        rowSpan={unit.coreSkills.length}
                      ></div>
                      {unit.coreSkills.map((coreSkill, index) => (
                        <div key={index} className="skill-row">
                          {index === 0 ? (
                            <div className="unit-name-cell">
                              <span
                                className="unit-name"
                                style={{ marginLeft: "10px" }}
                              >
                                {unit.name}
                              </span>
                            </div>
                          ) : (
                            <div className="unit-name-cell" />
                          )}

                          <div className="core-skill-cell">
                            <span className="core-skill-name">
                              {coreSkill.name}
                            </span>
                          </div>

                          <div className="soft-skills-cell">
                            {coreSkill.softSkills.map(
                              (softSkill, softIndex) => (
                                <span key={softIndex} className="em-skill-tag">
                                  {softSkill}
                                </span>
                              ),
                            )}
                          </div>

                          {index === 0 ? (
                            <div className="actions-cell">
                              <button
                                className="action-btn edit-btn"
                                onClick={() => handleEditBusinessUnit(unit.id)}
                                title={t("organizationInfo.editBusinessUnit")}
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                className="action-btn"
                                onClick={() =>
                                  handleDeleteBusinessUnit(unit.id)
                                }
                                title={t("organizationInfo.deleteBusinessUnit")}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="actions-cell" />
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {businessUnits.length > businessUnitsPerPage && (
                  <div className="business-units-pagination">
                    <div className="business-units-pagination-info">
                      {t("organizationInfo.showing")}{" "}
                      {indexOfFirstBusinessUnit + 1} {t("organizationInfo.to")}{" "}
                      {Math.min(indexOfLastBusinessUnit, businessUnits.length)}{" "}
                      {t("organizationInfo.of")} {businessUnits.length}{" "}
                      {t("organizationInfo.businessUnits")}
                    </div>
                    <div className="business-units-pagination-controls">
                      <button
                        className="business-units-pagination-btn"
                        onClick={() =>
                          setBusinessUnitsCurrentPage(
                            businessUnitsCurrentPage - 1,
                          )
                        }
                        disabled={businessUnitsCurrentPage === 1}
                      >
                        {t("organizationInfo.previous")}
                      </button>
                      <span className="business-units-pagination-info">
                        {t("organizationInfo.page")} {businessUnitsCurrentPage}{" "}
                        {t("organizationInfo.of")} {totalBusinessUnitsPages}
                      </span>
                      <button
                        className="business-units-pagination-btn"
                        onClick={() =>
                          setBusinessUnitsCurrentPage(
                            businessUnitsCurrentPage + 1,
                          )
                        }
                        disabled={
                          businessUnitsCurrentPage === totalBusinessUnitsPages
                        }
                      >
                        {t("organizationInfo.next")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "hiring-partners" && (
            <div className="tab-content">
              <div className="mmh-org-hiring-tab">
                <section
                  className={`partner-collaboration-gate ${
                    partnerCollaboration.enabled ? "enabled" : ""
                  }`}
                >
                  <div className="partner-collaboration-gate-main">
                    <div className="partner-collaboration-gate-icon">
                      <Handshake size={24} />
                    </div>

                    <div className="partner-collaboration-gate-copy">
                      <span className="partner-collaboration-eyebrow">
                        Optional feature
                      </span>

                      <h2>Partner Collaboration</h2>

                      <p>
                        Work securely with trusted partner organizations to
                        support staffing, hiring, and role matching.
                      </p>

                      <p>
                        You remain in control of which profiles, job roles, and
                        related information are shared with each partner.
                      </p>
                    </div>

                    <div className="partner-collaboration-toggle-control">
                      <span>
                        {isUpdatingPartnerCollaboration
                          ? "Updating..."
                          : partnerCollaboration.enabled
                            ? "Enabled"
                            : "Disabled"}
                      </span>

                      <button
                        // disabled={!partnerAccess.canEditPartnerCollaboration}
                        type="button"
                        className={`partner-switch ${
                          partnerCollaboration.enabled ? "on" : ""
                        }`}
                        role="switch"
                        aria-checked={partnerCollaboration.enabled}
                        aria-label={`${
                          partnerCollaboration.enabled ? "Disable" : "Enable"
                        } Partner Collaboration`}
                        disabled={
                          isUpdatingPartnerCollaboration ||
                          !partnerAccess.canEnablePartnerCollaboration
                        }
                        onClick={() => {
                          if (partnerCollaboration.enabled) {
                            setShowDisablePartnerCollaborationModal(true);
                            return;
                          }

                          handleEnablePartnerCollaboration();
                        }}
                      >
                        <span />
                      </button>
                    </div>
                  </div>

                  <div className="partner-collaboration-benefits">
                    <div>
                      <Building size={17} />

                      <span>
                        <strong>Access Hiring tools</strong>
                        Match available roles with best-fit profiles
                      </span>
                    </div>

                    <div>
                      <Save size={17} />

                      <span>
                        <strong>Share selected information</strong>
                        Share only the profiles and job roles you approve
                      </span>
                    </div>

                    <div>
                      <Handshake size={17} />

                      <span>
                        <strong>Control partner visibility</strong>
                        Manage what each connected partner can access
                      </span>
                    </div>
                  </div>

                  {!partnerCollaboration.enabled && (
                    <div className="partner-collaboration-disabled-note">
                      Partner connections and Hiring remain unavailable until
                      this feature is enabled.
                    </div>
                  )}
                </section>

                {partnerCollaboration.enabled && (
                  <section className="partner-directory-refresh">
                    <div className="mmh-vendor-header">
                      <div>
                        <h3>Partner Directory</h3>
                        <p>
                          Manage partner connections, invitations, and
                          controlled data sharing from one scalable list.
                        </p>
                      </div>

                      <button
                        disabled={!partnerAccess.canEditPartner}
                        // disabled={!partnerAccess.canAddPartner}
                        className="mmh-btn mmh-btn-primary"
                        onClick={openAddPartnerModal}
                      >
                        Add Partner
                      </button>
                    </div>

                    <div className="partner-directory-smart-controls">
                      <div className="smart-filter-panel-teams">
                        <div
                          className={`smart-filter-top-bar ${
                            showPartnerDirectorySearch ? "search-open" : ""
                          }`}
                        >
                          {showPartnerDirectorySearch && (
                            <form
                              className="smart-search-inline-row"
                              onSubmit={(event) => {
                                event.preventDefault();
                                setPartnerDirectorySearch(
                                  partnerDirectorySearchInput.trim(),
                                );
                                setPartnerCurrentPage(1);
                              }}
                            >
                              <input
                                type="text"
                                value={partnerDirectorySearchInput}
                                onChange={(event) =>
                                  setPartnerDirectorySearchInput(
                                    event.target.value,
                                  )
                                }
                                placeholder="Search partners"
                                className="smart-search-inline-input"
                                autoFocus
                              />

                              <button
                                type="submit"
                                className="smart-filter-icon-btn active"
                                title="Search"
                                aria-label="Search partners"
                              >
                                <Search size={18} />
                              </button>

                              {(partnerDirectorySearchInput ||
                                partnerDirectorySearch) && (
                                <button
                                  type="button"
                                  className="smart-search-clear"
                                  onClick={() => {
                                    setPartnerDirectorySearchInput("");
                                    setPartnerDirectorySearch("");
                                    setPartnerCurrentPage(1);
                                  }}
                                  title="Clear search"
                                  aria-label="Clear partner search"
                                >
                                  <X size={18} />
                                </button>
                              )}
                            </form>
                          )}

                          <div className="smart-filter-icons-right">
                            {!showPartnerDirectorySearch && (
                              <button
                                type="button"
                                className="smart-filter-icon-btn"
                                onClick={() => {
                                  setPartnerDirectorySearchInput(
                                    partnerDirectorySearch,
                                  );
                                  setShowPartnerDirectorySearch(true);
                                  setShowPartnerDirectoryFilters(false);
                                }}
                                title="Search"
                                aria-label="Open partner search"
                              >
                                <Search size={18} />
                              </button>
                            )}

                            <div className="smart-filter-separator" />

                            <button
                              type="button"
                              className={`smart-filter-icon-btn ${
                                showPartnerDirectoryFilters ? "active" : ""
                              }`}
                              onClick={() => {
                                setShowPartnerDirectoryFilters((previous) => {
                                  const nextValue = !previous;
                                  if (nextValue) {
                                    setShowPartnerDirectorySearch(false);
                                  }
                                  return nextValue;
                                });
                              }}
                              title="Filters"
                              aria-label="Toggle partner filters"
                            >
                              <Filter size={18} />
                            </button>
                          </div>

                          <div className="smart-filter-info">
                            <span className="partner-directory-result-count">
                              {partnerTotalCount} partner
                              {partnerTotalCount === 1 ? "" : "s"}
                            </span>

                            {getAppliedPartnerFiltersCount() > 0 && (
                              <>
                                <span className="smart-filter-badge">
                                  {getAppliedPartnerFiltersCount()}
                                </span>
                                <span>filters applied</span>
                                <button
                                  type="button"
                                  className="partner-directory-smart-clear"
                                  onClick={clearPartnerDirectoryFilters}
                                >
                                  Clear all
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {showPartnerDirectoryFilters && (
                          <>
                            <div className="smart-filter-divider" />
                            <div className="smart-filter-content">
                              <div className="smart-filter-grid">
                                <div className="smart-filter-item">
                                  <label>Status</label>
                                  <select
                                    value={partnerStatusFilter}
                                    onChange={(event) => {
                                      setPartnerStatusFilter(
                                        event.target.value,
                                      );
                                      setPartnerCurrentPage(1);
                                    }}
                                    className={`smart-filter-select ${
                                      partnerStatusFilter !== "All"
                                        ? "smart-active"
                                        : ""
                                    }`}
                                  >
                                    <option value="All">All statuses</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                    <option value="Expired">Expired</option>
                                  </select>
                                </div>

                                <div className="smart-filter-item">
                                  <label>Invitation Direction</label>
                                  <select
                                    value={partnerDirectionFilter}
                                    onChange={(event) => {
                                      setPartnerDirectionFilter(
                                        event.target.value,
                                      );
                                      setPartnerCurrentPage(1);
                                    }}
                                    className={`smart-filter-select ${
                                      partnerDirectionFilter !== "All"
                                        ? "smart-active"
                                        : ""
                                    }`}
                                  >
                                    <option value="All">All directions</option>
                                    <option value="Received">Received</option>
                                    <option value="Sent">Sent</option>
                                  </select>
                                </div>

                                <div className="smart-filter-item">
                                  <label>Shared Data</label>
                                  <select
                                    value={partnerSharedDataFilter}
                                    onChange={(event) => {
                                      setPartnerSharedDataFilter(
                                        event.target.value,
                                      );
                                      setPartnerCurrentPage(1);
                                    }}
                                    className={`smart-filter-select ${
                                      partnerSharedDataFilter !== "All"
                                        ? "smart-active"
                                        : ""
                                    }`}
                                  >
                                    <option value="All">Any shared data</option>
                                    <option value="Profiles">Profiles</option>
                                    <option value="Job Roles">Job Roles</option>
                                    <option value="Departments">
                                      Departments
                                    </option>
                                    <option value="None">None</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="u-page-actions partner-directory-bulk-toolbar">
                      <label className="select-all-wrap partner-directory-select-all">
                        <input
                          type="checkbox"
                          checked={allCurrentPartnersSelected}
                          ref={(input) => {
                            if (input) {
                              input.indeterminate = someCurrentPartnersSelected;
                            }
                          }}
                          disabled={
                            isPartnersLoading ||
                            currentPartnerPageIds.length === 0
                          }
                          onChange={(event) =>
                            toggleAllPartnersOnCurrentPage(event.target.checked)
                          }
                          aria-label="Select all active partners on this page"
                        />

                        <span>
                          Select All
                          {allCurrentPartnersSelected &&
                          currentPartnerPageIds.length
                            ? ` (${currentPartnerPageIds.length})`
                            : ""}
                        </span>
                      </label>

                      <BulkAction
                        options={[
                          {
                            value: "delete-partners",
                            label: "Remove Connection",
                            // disabled: !partnerAccess.canBulkRemovePartners,
                          },
                        ]}
                        selectedIds={selectedActivePartnerIds}
                        onAction={(action) => {
                          if (
                            action === "delete-partners" &&
                            selectedActivePartnerIds.length > 0
                          ) {
                            setSinglePartnerIdToRemove(null);
                            setShowPartnerBulkDeleteModal(true);
                          }
                        }}
                        label="Bulk Action"
                        placeholder="Select bulk action"
                        requireSelection={true}
                      />
                    </div>

                    <div className="mmh-vendor-table-container partner-directory-table-wrap">
                      <table className="mmh-vendor-table partner-directory-table">
                        <thead>
                          <tr>
                            <th className="partner-directory-select-column">
                              <input
                                type="checkbox"
                                checked={allCurrentPartnersSelected}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate =
                                      someCurrentPartnersSelected;
                                  }
                                }}
                                onChange={(event) =>
                                  toggleAllPartnersOnCurrentPage(
                                    event.target.checked,
                                  )
                                }
                                aria-label="Select all partners on this page"
                              />
                            </th>
                            <th>Partner Organization</th>
                            <th>Partner Super Admin</th>
                            <th>Shared Data</th>
                            <th>Shared by partner</th>

                            <th>
                              <button
                                type="button"
                                className="partner-directory-sort-header"
                                onClick={() => togglePartnerSort("lastUpdated")}
                              >
                                Last Updated
                                {renderPartnerSortChevrons("lastUpdated")}
                              </button>
                            </th>

                            <th className="partner-directory-status-column">
                              <button
                                type="button"
                                className="partner-directory-sort-header"
                                onClick={() => togglePartnerSort("status")}
                              >
                                Status
                                {renderPartnerSortChevrons("status")}
                              </button>
                            </th>

                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {isPartnersLoading ? (
                            <tr className="mmh-vendor-empty-row">
                              <td colSpan={8}>Loading partners...</td>
                            </tr>
                          ) : visiblePartners.length > 0 ? (
                            visiblePartners.map((partner) => {
                              const actionMeta = getPartnerActionMeta(partner);

                              const directionMeta =
                                getPartnerDirectionMeta(partner);

                              const normalizedStatus = String(
                                partner.status || "Unknown",
                              );

                              const statusLabel = isExpiredPartnerInvitation(
                                partner,
                              )
                                ? "Expired"
                                : normalizedStatus;

                              const statusClass = normalizedStatus
                                .trim()
                                .toLowerCase()
                                .replace(/\s+/g, "-");

                              const canRemoveConnection =
                                normalizedStatus.trim().toUpperCase() ===
                                "ACTIVE";

                              const showPendingDot =
                                normalizedStatus.trim().toUpperCase() ===
                                  "PENDING" &&
                                String(partner?.invitation || partner?.direction || "")
                                  .trim()
                                  .toUpperCase() === "RECEIVED";

                              return (
                                <tr
                                  key={partner.id}
                                  className={showPendingDot ? "unread" : ""}
                                  onClick={() =>
                                    openPartnerActionModal(partner)
                                  }
                                >
                                  <td
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        canRemoveConnection &&
                                        selectedPartnerIds.includes(partner.id)
                                      }
                                      disabled={!canRemoveConnection}
                                      onChange={(event) =>
                                        togglePartnerSelection(
                                          partner.id,
                                          event.target.checked,
                                        )
                                      }
                                      aria-label={`Select ${partner.name}`}
                                      title={
                                        canRemoveConnection
                                          ? "Select active connection"
                                          : "Only active connections can be removed"
                                      }
                                    />
                                  </td>

                                  <td>
                                    <div className="partner-directory-org-cell">
                                      <span
                                        className="partner-directory-dot-slot"
                                        aria-hidden={!showPendingDot}
                                      >
                                        {showPendingDot && (
                                          <span
                                            className="partner-notification-dot row-dot"
                                            aria-label="Pending partner invitation"
                                          />
                                        )}
                                      </span>

                                      <div className="partner-directory-org-copy">
                                        <div className="partner-directory-name-line">
                                          <strong>{partner.name}</strong>
                                        </div>
                                        <div className="partner-table-subtext">
                                          {partner.organizationCode}
                                        </div>
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    {partner.contactPerson}

                                    <div className="partner-table-subtext">
                                      {partner.contactEmail}
                                    </div>
                                  </td>

                                  <td>
                                    <span className="partner-shared-data-text">
                                      {getPartnerSharedDataText(partner)}
                                    </span>
                                  </td>

                                  <td>
                                    <span className="partner-shared-data-text">
                                      {getByPartnerSharedDataText(partner)}
                                    </span>
                                  </td>

                                  <td>
                                    {formatEmployeeCreatedAt(
                                      partner.lastUpdated,
                                    ) || "-"}
                                  </td>

                                  <td className="partner-directory-status-cell">
                                    <div className="partner-directory-status-stack">
                                      <div className="partner-directory-status-line">
                                        {directionMeta.icon && (
                                          <span
                                            className={`partner-direction-indicator ${
                                              directionMeta.tone || ""
                                            }`}
                                            title={directionMeta.label}
                                            aria-label={directionMeta.label}
                                          >
                                            {directionMeta.icon}
                                          </span>
                                        )}

                                        <span
                                          className={`mmh-vendor-status ${statusClass}`}
                                        >
                                          {statusLabel}
                                        </span>
                                      </div>

                                      {normalizedStatus.trim().toUpperCase() ===
                                        "ACTIVE" && (
                                        <span className="partner-active-since">
                                          Since{" "}
                                          {formatPartnerDate(
                                            getPartnerConnectionSinceDate(
                                              partner,
                                            ),
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  </td>

                                  <td
                                    className="partner-directory-action-cell"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <div className="partner-directory-row-actions">
                                      <button
                                        type="button"
                                        className="partner-directory-row-action"
                                        onClick={() =>
                                          openPartnerActionModal(partner)
                                        }
                                      >
                                        <strong>{actionMeta.label}</strong>
                                        <span>{actionMeta.helper}</span>
                                      </button>

                                      {canRemoveConnection && (
                                        <button
                                          disabled={
                                            !partnerAccess.canDeletePartner
                                          }
                                          type="button"
                                          className="partner-directory-remove-row-btn"
                                          onClick={() => {
                                            setSinglePartnerIdToRemove(
                                              partner.id,
                                            );
                                            setShowPartnerBulkDeleteModal(true);
                                          }}
                                          aria-label={`Remove connection with ${partner.name}`}
                                          title="Remove Connection"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr className="mmh-vendor-empty-row">
                              <td colSpan={8}>
                                No partners match the current search and
                                filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {partnerTotalCount > 0 && (
                      <Pagination
                        loading={isPartnersLoading}
                        total={partnerTotalCount}
                        currentPage={partnerCurrentPage}
                        itemsPerPage={partnerItemsPerPage}
                        currentPageCount={visiblePartners.length}
                        onPageChange={(page) => {
                          setPartnerCurrentPage(page);
                        }}
                        pageSizeOptions={[10, 20, 40, 60, 100]}
                        onPageSizeChange={(size) => {
                          setPartnerItemsPerPage(size);
                          setPartnerCurrentPage(1);
                        }}
                        infoRenderer={({ start, end, total }) =>
                          `Showing ${start}-${end} of ${total} partners`
                        }
                      />
                    )}
                  </section>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <div
        className={`mmh-org-modal-overlay ${
          partnerViewModalOpen ? "open" : ""
        }`}
      >
        <div className="mmh-org-modal-content partner-inactive-modal">
          {selectedPartner && (
            <>
              <div className="mmh-org-modal-header partner-view-header">
                <div>
                  <span className="partner-view-eyebrow">
                    Inactive Connection Details
                  </span>

                  <h2>{selectedPartner.name}</h2>
                </div>

                <button
                  type="button"
                  className="mmh-org-drawer-close"
                  onClick={() => setPartnerViewModalOpen(false)}
                  aria-label="Close inactive connection details"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mmh-org-modal-body partner-inactive-body">
                <div className="partner-inactive-hero">
                  <span>
                    <Unlink size={22} />
                  </span>

                  <div>
                    <h3>Connection is inactive</h3>

                    <p>
                      This connection can no longer be used for sharing
                      profiles, job roles, or Hiring information.
                    </p>
                  </div>
                </div>

                <div className="partner-inactive-detail-grid">
                  <div>
                    <span>Partner Organization</span>
                    <strong>{selectedPartner.name || "-"}</strong>
                  </div>

                  <div>
                    <span>Organization Code</span>
                    <strong>{selectedPartner.organizationCode || "-"}</strong>
                  </div>

                  <div>
                    <span>Inactive Reason</span>
                    <strong>{getInactiveReasonLabel(selectedPartner)}</strong>
                  </div>

                  <div>
                    <span>Date Status Changed</span>
                    <strong>
                      {formatPartnerDate(
                        getInactiveStatusChangedDate(selectedPartner),
                      )}
                    </strong>
                  </div>

                  {getInactiveResponsibleOrganization(selectedPartner) && (
                    <div>
                      <span>Action by Organization</span>

                      <strong>
                        {getInactiveResponsibleOrganization(selectedPartner)}
                      </strong>
                    </div>
                  )}

                  {getInactiveResponsibleUser(selectedPartner) && (
                    <div>
                      <span>Action by User</span>

                      <strong>
                        {getInactiveResponsibleUser(selectedPartner)}
                      </strong>
                    </div>
                  )}
                </div>

                {getInactiveDescription(selectedPartner) && (
                  <div className="partner-inactive-description">
                    <span>Description / Decline Reason</span>

                    <p>{getInactiveDescription(selectedPartner)}</p>
                  </div>
                )}

                <div className="partner-inactive-readonly-note">
                  <Info size={17} />

                  <p>
                    Inactive connections are read-only and cannot be restored
                    from this view. A new invitation must be sent and accepted
                    to reconnect with this organization.
                  </p>
                </div>
              </div>

              <div className="mmh-org-modal-footer">
                <button
                  type="button"
                  className="mmh-btn mmh-btn-secondary"
                  onClick={() => setPartnerViewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className={`mmh-org-modal-overlay ${addPartnerModalOpen ? "open" : ""}`}
      >
        <div className="mmh-org-modal-content partner-form-modal">
          <div className="mmh-org-modal-header">
            <div>
              <span className="partner-view-eyebrow">Partner Directory</span>
              <h2>Add Partner</h2>
            </div>
            <button
              className="mmh-org-drawer-close"
              onClick={() => {
                setAddPartnerModalOpen(false);
                setPartnerCodeError("");
              }}
            >
              <X size={20} />
            </button>
          </div>

          <div className="mmh-org-modal-body">
            {renderPartnerFormFields(newPartner, setNewPartner, {
              mode: "add",
              allowCodeEdit: true,
              showInvitationNote: true,
            })}
          </div>

          <div className="mmh-org-modal-footer">
            <button
              className="mmh-btn mmh-btn-secondary"
              onClick={() => setAddPartnerModalOpen(false)}
            >
              Cancel
            </button>
            <button
              disabled={!partnerAccess.canEditPartner}
              className="mmh-btn mmh-btn-primary"
              onClick={handleSavePartner}
              disabled={
                isSendingPartnerInvite || !isPartnerFormValid(newPartner)
              }
            >
              {isSendingPartnerInvite ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`mmh-org-modal-overlay ${
          partnerManageModalOpen ? "open" : ""
        }`}
      >
        <div
          className={`mmh-org-modal-content partner-manage-modal ${
            partnerActionMode !== "manage" ? "partner-invite-modal" : ""
          }`}
        >
          {managingPartner && (
            <>
              <header className="mmh-org-modal-header partner-manage-header">
                <div className="partner-manage-header-copy">
                  <div className="partner-manage-breadcrumb">
                    <span>Organization Info</span>

                    <ChevronRight size={13} />

                    <span>Partners</span>

                    <ChevronRight size={13} />

                    <strong>{getPartnerActionModalTitle()}</strong>
                  </div>

                  <h2>{getPartnerActionModalTitle()}</h2>

                  <p>{managingPartner.name}</p>
                </div>

                <button
                  type="button"
                  className="mmh-org-drawer-close"
                  onClick={closePartnerActionModal}
                  aria-label="Close partner modal"
                >
                  <X size={20} />
                </button>
              </header>

              <div className="mmh-org-modal-body partner-manage-body">
                {partnerActionMode === "review" && (
                  <div className="partner-invite-flow">
                    {inviteDecision === "" && (
                      <>
                        <section className="partner-invite-hero">
                          <h3>
                            {managingPartner.name} invited your organization to
                            collaborate as a partner.
                          </h3>

                          <p>
                            Review the invitation details and planned sharing
                            permissions before accepting the connection.
                          </p>
                        </section>

                        <section className="partner-modal-section">
                          <div className="partner-invite-section-heading">
                            <h3>Invitation details</h3>

                            <p>
                              The invitation remains pending until it is
                              accepted or declined.
                            </p>
                          </div>

                          <div className="partner-invite-grid">
                            <div>
                              <span>Partner Organization</span>

                              <strong>{managingPartner.name || "-"}</strong>
                            </div>

                            <div>
                              <span>Sent By</span>

                              <strong>
                                {managingPartner.sentBy ||
                                  managingPartner.contactPerson ||
                                  "-"}
                              </strong>
                            </div>

                            <div>
                              <span>Email</span>

                              <strong>
                                {managingPartner.sentByEmail ||
                                  managingPartner.contactEmail ||
                                  "-"}
                              </strong>
                            </div>

                            <div>
                              <span>Date Sent</span>

                              <strong>
                                {formatPartnerDate(managingPartner.dateSent)}
                              </strong>
                            </div>

                            <div>
                              <span>Invitation Expires</span>

                              <strong>
                                {formatPartnerDate(managingPartner.expiredAt)}
                              </strong>
                            </div>
                          </div>
                        </section>

                        <section className="partner-modal-section">
                          <div className="partner-invite-section-heading">
                            <h3>Planned sharing permissions</h3>

                            <p>
                              These settings become active only after the
                              invitation is accepted.
                            </p>
                          </div>

                          <div className="partner-sharing-summary-grid partner-invite-summary">
                            <div>
                              <span>Profiles</span>
                              <p>
                                {managingPartner.shareProfiles
                                  ? "Selected"
                                  : "Not selected"}
                              </p>
                            </div>

                            <div>
                              <span>Profile Identity</span>
                              <p>
                                {managingPartner.protectProfileIdentity
                                  ? "Masked"
                                  : "Visible"}
                              </p>
                            </div>

                            <div>
                              <span>Job Roles</span>
                              <p>
                                {managingPartner.shareJobRoles
                                  ? "Selected"
                                  : "Not selected"}
                              </p>
                            </div>

                            <div>
                              <span>Departments</span>
                              <p>
                                {managingPartner.shareDepartments
                                  ? "Selected"
                                  : "Not selected"}
                              </p>
                            </div>
                          </div>
                        </section>

                        <div className="partner-legal-note">
                          <Info size={18} />

                          <p>
                            Participating organizations are responsible for the
                            authorization, accuracy and quality of shared data.
                            MayaMaya does not independently verify or guarantee
                            user-provided data.
                          </p>
                        </div>

                        <div className="partner-email-notice">
                          Both organizations&apos; Super Admins will be notified
                          by email when this invitation is accepted or declined.
                        </div>
                      </>
                    )}

                    {inviteDecision === "accept" && (
                      <section className="partner-decision-card">
                        <div className="partner-decision-icon accept">
                          <Handshake size={22} />
                        </div>

                        <h3>Accept Partner Invitation</h3>

                        <p>
                          Accepting will create an active partner connection
                          with {managingPartner.name}. The sharing permissions
                          can be managed after the connection becomes active.
                        </p>

                        <div className="partner-invite-grid compact">
                          <div>
                            <span>Partner Organization</span>

                            <strong>{managingPartner.name}</strong>
                          </div>

                          <div>
                            <span>Date Sent</span>

                            <strong>
                              {formatPartnerDate(managingPartner.dateSent)}
                            </strong>
                          </div>
                        </div>
                      </section>
                    )}

                    {inviteDecision === "decline" && (
                      <section className="partner-decision-card">
                        <div className="partner-decision-icon decline">
                          <X size={22} />
                        </div>

                        <h3>Decline Partner Invitation</h3>

                        <p>
                          The invitation will become inactive. You may provide
                          an optional explanation for the sending organization.
                        </p>

                        <label className="mmh-form-label">
                          Optional decline reason
                        </label>

                        <textarea
                          className="mmh-form-textarea"
                          value={declineReason}
                          onChange={(event) =>
                            setDeclineReason(event.target.value)
                          }
                          placeholder="Add an optional explanation for the sending organization"
                        />
                      </section>
                    )}
                  </div>
                )}

                {partnerActionMode === "track" && (
                  <div className="partner-invite-flow">
                    <section className="partner-invite-hero">
                      <h3>
                        {isExpiredPartnerInvitation(managingPartner)
                          ? "Invitation Expired"
                          : "Invitation Sent"}
                      </h3>

                      <p>
                        {isExpiredPartnerInvitation(managingPartner)
                          ? "This invitation has expired. Resend the invitation to generate a new invitation and expiry date."
                          : "This request is waiting for a response from the partner organization. No sharing begins until the invitation is accepted."}
                      </p>
                    </section>

                    <section className="partner-modal-section">
                      <div className="partner-invite-section-heading">
                        <h3>Invitation details</h3>

                        <p>
                          Resending the invitation notifies the partner again
                          and refreshes the expiry date.
                        </p>
                      </div>

                      <div className="partner-invite-grid">
                        <div>
                          <span>Partner Organization</span>

                          <strong>{managingPartner.name || "-"}</strong>
                        </div>

                        <div>
                          <span>Sent By</span>

                          <strong>
                            {managingPartner.sentBy ||
                              managingPartner.contactPerson ||
                              "-"}
                          </strong>
                        </div>

                        <div>
                          <span>Email</span>

                          <strong>
                            {managingPartner.sentByEmail ||
                              managingPartner.contactEmail ||
                              "-"}
                          </strong>
                        </div>

                        <div>
                          <span>Date Sent</span>

                          <strong>
                            {formatPartnerDate(managingPartner.dateSent)}
                          </strong>
                        </div>

                        <div>
                          <span>Invitation Expires</span>

                          <strong>
                            {formatPartnerDate(managingPartner.expiredAt)}
                          </strong>
                        </div>
                      </div>
                    </section>

                    <section className="partner-modal-section">
                      <div className="partner-invite-section-heading">
                        <h3>Planned sharing permissions</h3>

                        <p>
                          These permissions remain inactive until the receiving
                          organization accepts the invitation.
                        </p>
                      </div>

                      <div className="partner-sharing-summary-grid partner-invite-summary">
                        <div>
                          <span>Profiles</span>

                          <p>
                            {managingPartner.shareProfiles
                              ? "Selected"
                              : "Not selected"}
                          </p>
                        </div>

                        <div>
                          <span>Profile Identity</span>

                          <p>
                            {managingPartner.protectProfileIdentity
                              ? "Masked"
                              : "Visible"}
                          </p>
                        </div>

                        <div>
                          <span>Job Roles</span>

                          <p>
                            {managingPartner.shareJobRoles
                              ? "Selected"
                              : "Not selected"}
                          </p>
                        </div>

                        <div>
                          <span>Departments</span>

                          <p>
                            {managingPartner.shareDepartments
                              ? "Selected"
                              : "Not selected"}
                          </p>
                        </div>
                      </div>
                    </section>

                    <div className="partner-track-sharing-note">
                      <Info size={17} />

                      <p>
                        No profile, job-role or department information is
                        currently accessible to the receiving organization.
                      </p>
                    </div>
                  </div>
                )}

                {partnerActionMode === "manage" && (
                  <div className="partner-manage-flow">
                    <section className="partner-manage-intro">
                      <h3>Manage what is shared with this partner</h3>

                      <p>
                        Update sharing capabilities and select the profiles or
                        job roles that this partner can access.
                      </p>
                    </section>

                    <section className="partner-manage-capabilities">
                      <div className="partner-manage-capabilities-header">
                        <h3>Sharing capabilities</h3>

                        <p>
                          Control which types of information can be shared with
                          this partner.
                        </p>
                      </div>

                      <div className="partner-manage-capabilities-grid">
                        <div className="partner-manage-capability">
                          <div>
                            <strong>Share Profiles</strong>

                            <span>
                              Allow selected organization profiles to be shared.
                            </span>
                          </div>

                          {renderPartnerToggle({
                            checked: Boolean(managingPartner.shareProfiles),
                            disabled: !partnerAccess.canEditPartner,
                            onChange: (checked) =>
                              handleManagedCapabilityToggle(
                                "profiles",
                                checked,
                              ),
                          })}
                        </div>

                        <div
                          className={`partner-manage-capability ${
                            !managingPartner.shareProfiles ? "disabled" : ""
                          }`}
                        >
                          <div>
                            <strong>Protect Profile Identity</strong>

                            <span>
                              Show protected profile IDs instead of personal
                              identifying details.
                            </span>
                          </div>

                          {renderPartnerToggle({
                            checked: Boolean(
                              managingPartner.protectProfileIdentity,
                            ),
                            disabled:
                              !partnerAccess.canEditPartner ||
                              !managingPartner.shareProfiles,
                            onChange: (checked) =>
                              setManagingPartner((previousPartner) => ({
                                ...previousPartner,

                                protectProfileIdentity: checked,
                              })),
                          })}
                        </div>

                        <div className="partner-manage-capability">
                          <div>
                            <strong>Share Job Roles</strong>

                            <span>Allow selected job roles to be shared.</span>
                          </div>

                          {renderPartnerToggle({
                            checked: Boolean(managingPartner.shareJobRoles),
                            disabled: !partnerAccess.canEditPartner,
                            onChange: (checked) =>
                              handleManagedCapabilityToggle(
                                "jobRoles",
                                checked,
                              ),
                          })}
                        </div>

                        <div
                          className={`partner-manage-capability ${
                            !managingPartner.shareJobRoles ? "disabled" : ""
                          }`}
                        >
                          <div>
                            <strong>Share Department</strong>

                            <span>
                              Include department labels connected to shared job
                              roles.
                            </span>
                          </div>

                          {renderPartnerToggle({
                            checked: Boolean(managingPartner.shareDepartments),
                            disabled:
                              !partnerAccess.canEditPartner ||
                              !managingPartner.shareJobRoles,
                            onChange: (checked) =>
                              setManagingPartner((previousPartner) => ({
                                ...previousPartner,

                                shareDepartments: checked,
                              })),
                          })}
                        </div>
                      </div>
                    </section>

                    {(managingPartner.shareProfiles ||
                      managingPartner.shareJobRoles) && (
                      <div
                        className="partner-manage-tabs"
                        role="tablist"
                        aria-label="Partner sharing sections"
                      >
                        {managingPartner.shareProfiles && (
                          <button
                            type="button"
                            role="tab"
                            aria-selected={manageActiveTab === "profiles"}
                            className={
                              manageActiveTab === "profiles" ? "active" : ""
                            }
                            onClick={() => setManageActiveTab("profiles")}
                          >
                            Profiles
                          </button>
                        )}

                        {managingPartner.shareJobRoles && (
                          <button
                            type="button"
                            role="tab"
                            aria-selected={manageActiveTab === "jobRoles"}
                            className={
                              manageActiveTab === "jobRoles" ? "active" : ""
                            }
                            onClick={() => setManageActiveTab("jobRoles")}
                          >
                            Job Roles
                          </button>
                        )}
                      </div>
                    )}

                    {manageActiveTab === "profiles" &&
                      managingPartner.shareProfiles && (
                        <section className="partner-manage-section">
                          <div className="partner-selected-summary">
                            {/*<strong>Profiles shared</strong>*/}

                            <span>
                              {managingPartner.protectProfileIdentity
                                ? "Partner will see protected IDs instead of names and email addresses. "
                                : "Partner can see the permitted profile identity details. "}
                              To add more profiles, go to People &gt;{" "}
                              <a
                                href="/console/employees"
                                style={{
                                  color: "inherit",
                                  fontWeight: 700,
                                  textDecoration: "underline",
                                }}
                              >
                                Profiles
                              </a>
                              .
                            </span>
                          </div>

                          <div className="partner-profile-sharing-note">
                            <Info size={16} />

                            <p>
                              Partners can view skill-level details but cannot
                              see the profile&apos;s current role information.
                            </p>
                          </div>

                          <div className="partner-directory-smart-controls partner-manage-smart-controls">
                            <div className="smart-filter-shell">
                              <div className="smart-filter-top-bar">
                                {showManageProfileSearch && (
                                  <form
                                    className="smart-search-inline-row"
                                    onSubmit={(event) => {
                                      event.preventDefault();
                                      setShowManageProfileSearch(false);
                                    }}
                                  >
                                    <Search size={17} />

                                    <input
                                      type="text"
                                      className="smart-search-inline-input"
                                      placeholder="Search shared profiles"
                                      value={manageProfileSearch}
                                      autoFocus
                                      onChange={(event) =>
                                        setManageProfileSearch(
                                          event.target.value,
                                        )
                                      }
                                    />

                                    <button
                                      type="submit"
                                      className="smart-search-submit"
                                      aria-label="Apply profile search"
                                      title="Search"
                                    >
                                      <Search size={17} />
                                    </button>

                                    {manageProfileSearch && (
                                      <button
                                        type="button"
                                        className="smart-search-clear"
                                        onClick={() =>
                                          setManageProfileSearch("")
                                        }
                                        aria-label="Clear profile search"
                                        title="Clear search"
                                      >
                                        <X size={18} />
                                      </button>
                                    )}
                                  </form>
                                )}

                                <div className="smart-filter-icons-right">
                                  {!showManageProfileSearch && (
                                    <button
                                      type="button"
                                      className={`smart-filter-icon-btn ${
                                        manageProfileSearch ? "active" : ""
                                      }`}
                                      onClick={() => {
                                        setShowManageProfileSearch(true);
                                        setShowManageProfileFilters(false);
                                      }}
                                      title="Search profiles"
                                      aria-label="Open profile search"
                                    >
                                      <Search size={18} />
                                    </button>
                                  )}

                                  <div className="smart-filter-separator" />

                                  <button
                                    type="button"
                                    className={`smart-filter-icon-btn ${
                                      showManageProfileFilters ? "active" : ""
                                    }`}
                                    onClick={() => {
                                      setShowManageProfileFilters(
                                        (previous) => {
                                          const nextValue = !previous;

                                          if (nextValue) {
                                            setShowManageProfileSearch(false);
                                          }

                                          return nextValue;
                                        },
                                      );
                                    }}
                                    title="Profile filters"
                                    aria-label="Toggle profile filters"
                                  >
                                    <Filter size={18} />
                                  </button>
                                </div>

                                <div className="smart-filter-info">
                                  <span className="partner-directory-result-count">
                                    {filteredManageProfiles.length} shared
                                    profile
                                    {filteredManageProfiles.length === 1
                                      ? ""
                                      : "s"}
                                  </span>

                                  {getAppliedManageProfileFiltersCount() >
                                    0 && (
                                    <>
                                      <span className="smart-filter-badge">
                                        {getAppliedManageProfileFiltersCount()}
                                      </span>

                                      <span>filters applied</span>

                                      <button
                                        type="button"
                                        className="partner-directory-smart-clear"
                                        onClick={clearManageProfileFilters}
                                      >
                                        Clear all
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {showManageProfileFilters && (
                                <>
                                  <div className="smart-filter-divider" />

                                  <div className="smart-filter-content">
                                    <div className="smart-filter-grid">
                                      <div className="smart-filter-item">
                                        <label>Department</label>

                                        <select
                                          value={manageProfileDepartmentFilter}
                                          onChange={(event) =>
                                            setManageProfileDepartmentFilter(
                                              event.target.value,
                                            )
                                          }
                                          className={`smart-filter-select ${
                                            manageProfileDepartmentFilter !==
                                            profileAllOption
                                              ? "smart-active"
                                              : ""
                                          }`}
                                        >
                                          {profileDepartmentOptions.map(
                                            (option) => (
                                              <option
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {option.label}
                                              </option>
                                            ),
                                          )}
                                        </select>
                                      </div>

                                      <div className="smart-filter-item">
                                        <label>Level</label>

                                        <select
                                          value={manageProfileLevelFilter}
                                          onChange={(event) =>
                                            setManageProfileLevelFilter(
                                              event.target.value,
                                            )
                                          }
                                          className={`smart-filter-select ${
                                            manageProfileLevelFilter !==
                                            profileAllOption
                                              ? "smart-active"
                                              : ""
                                          }`}
                                        >
                                          {levels.map((option) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      <div className="smart-filter-item">
                                        <label>Availability</label>

                                        <select
                                          value={
                                            manageProfileAvailabilityFilter
                                          }
                                          onChange={(event) =>
                                            setManageProfileAvailabilityFilter(
                                              event.target.value,
                                            )
                                          }
                                          className={`smart-filter-select ${
                                            manageProfileAvailabilityFilter !==
                                            profileAllOption
                                              ? "smart-active"
                                              : ""
                                          }`}
                                        >
                                          {availabilityOptions.map((option) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="u-page-actions partner-directory-bulk-toolbar partner-manage-bulk-toolbar">
                            <label className="select-all-wrap partner-directory-select-all">
                              <input
                                type="checkbox"
                                checked={isAllVisibleProfilesSelected}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate =
                                      someVisibleProfilesSelected;
                                  }
                                }}
                                disabled={filteredManageProfiles.length === 0}
                                onChange={(event) =>
                                  toggleAllManagedProfiles(event.target.checked)
                                }
                                aria-label="Select all visible shared profiles"
                              />

                              <span>
                                Select All
                                {isAllVisibleProfilesSelected &&
                                filteredManageProfiles.length
                                  ? ` (${filteredManageProfiles.length})`
                                  : ""}
                              </span>
                            </label>

                            <BulkAction
                              options={[
                                {
                                  value: "remove-shared-profiles",
                                  label: "Remove",
                                  disabled:
                                    !partnerAccess.canBulkRemovePartners,
                                },
                              ]}
                              selectedIds={selectedManagedProfileIds}
                              onAction={(action) => {
                                if (action === "remove-shared-profiles") {
                                  handleBulkRemoveManagedProfiles();
                                }
                              }}
                              label="Bulk Action"
                              placeholder="Select bulk action"
                              requireSelection={true}
                            />
                          </div>

                          <div className="partner-share-table-wrap">
                            <div className="partner-share-table profiles">
                              <div className="partner-share-table-header">
                                <div>
                                  <input
                                    type="checkbox"
                                    checked={isAllVisibleProfilesSelected}
                                    ref={(input) => {
                                      if (input) {
                                        input.indeterminate =
                                          someVisibleProfilesSelected;
                                      }
                                    }}
                                    onChange={(event) =>
                                      toggleAllManagedProfiles(
                                        event.target.checked,
                                      )
                                    }
                                    aria-label="Select all visible profiles"
                                  />
                                </div>

                                <div>Profile</div>
                                <div>Email</div>
                                <div>Job Role</div>
                                <div>Department</div>
                                <div>Level</div>
                                <div>Availability</div>
                              </div>

                              {filteredManageProfiles.length > 0 ? (
                                filteredManageProfiles.map((profile) => {
                                  return (
                                    <div
                                      key={profile.id}
                                      className="partner-share-table-row"
                                    >
                                      <div>
                                        <input
                                          type="checkbox"
                                          checked={selectedManagedProfileIds.includes(
                                            profile.id,
                                          )}
                                          onChange={(event) =>
                                            toggleManagedProfileSelection(
                                              profile.id,
                                              event.target.checked,
                                            )
                                          }
                                          aria-label={`Select ${
                                            profile.name ||
                                            profile.protectedId ||
                                            profile.id
                                          }`}
                                        />
                                      </div>

                                      <div className="partner-share-name-cell">
                                        <strong>
                                          {managingPartner.protectProfileIdentity
                                            ? profile.protectedId
                                            : profile.name}
                                        </strong>

                                        <span>{profile.id}</span>
                                      </div>

                                      <div>
                                        {managingPartner.protectProfileIdentity
                                          ? "Protected"
                                          : profile.email}
                                      </div>

                                      <div>{profile.jobRole}</div>

                                      <div>{profile.department}</div>

                                      <div>{profile.level}</div>

                                      <div>
                                        <span
                                          className={`mmh-protected-badge mmh-protected-badge-${String(
                                            profile.availability || "unknown",
                                          )
                                            .replace(/\s+/g, "")
                                            .toLowerCase()}`}
                                        >
                                          {profile.availability}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="partner-share-empty">
                                  No profiles match the current filters.
                                </div>
                              )}
                            </div>
                          </div>
                        </section>
                      )}

                    {manageActiveTab === "jobRoles" &&
                      managingPartner.shareJobRoles && (
                        <section className="partner-manage-section">
                          <div className="partner-selected-summary">
                            {/*<strong>Job roles shared</strong>*/}

                            <span>
                              These are the job roles currently available to
                              this partner. To add more roles, go to Settings
                              &gt;{" "}
                              <a
                                href="/console/job-roles"
                                style={{
                                  color: "inherit",
                                  fontWeight: 700,
                                  textDecoration: "underline",
                                }}
                              >
                                Job Roles
                              </a>
                              .
                            </span>
                          </div>

                          <div className="partner-directory-smart-controls partner-manage-smart-controls">
                            <div className="smart-filter-shell">
                              <div className="smart-filter-top-bar">
                                {showManageJobSearch && (
                                  <form
                                    className="smart-search-inline-row"
                                    onSubmit={(event) => {
                                      event.preventDefault();
                                      setShowManageJobSearch(false);
                                    }}
                                  >
                                    <Search size={17} />

                                    <input
                                      type="text"
                                      className="smart-search-inline-input"
                                      placeholder="Search shared job roles"
                                      value={manageJobSearch}
                                      autoFocus
                                      onChange={(event) =>
                                        setManageJobSearch(event.target.value)
                                      }
                                    />

                                    <button
                                      type="submit"
                                      className="smart-search-submit"
                                      aria-label="Apply job role search"
                                      title="Search"
                                    >
                                      <Search size={17} />
                                    </button>

                                    {manageJobSearch && (
                                      <button
                                        type="button"
                                        className="smart-search-clear"
                                        onClick={() => setManageJobSearch("")}
                                        aria-label="Clear job role search"
                                        title="Clear search"
                                      >
                                        <X size={18} />
                                      </button>
                                    )}
                                  </form>
                                )}

                                <div className="smart-filter-icons-right">
                                  {!showManageJobSearch && (
                                    <button
                                      type="button"
                                      className={`smart-filter-icon-btn ${
                                        manageJobSearch ? "active" : ""
                                      }`}
                                      onClick={() => {
                                        setShowManageJobSearch(true);
                                        setShowManageJobFilters(false);
                                      }}
                                      title="Search job roles"
                                      aria-label="Open job role search"
                                    >
                                      <Search size={18} />
                                    </button>
                                  )}

                                  <div className="smart-filter-separator" />

                                  <button
                                    type="button"
                                    className={`smart-filter-icon-btn ${
                                      showManageJobFilters ? "active" : ""
                                    }`}
                                    onClick={() => {
                                      setShowManageJobFilters((previous) => {
                                        const nextValue = !previous;

                                        if (nextValue) {
                                          setShowManageJobSearch(false);
                                        }

                                        return nextValue;
                                      });
                                    }}
                                    title="Job role filters"
                                    aria-label="Toggle job role filters"
                                  >
                                    <Filter size={18} />
                                  </button>
                                </div>

                                <div className="smart-filter-info">
                                  <span className="partner-directory-result-count">
                                    {filteredManageJobRoles.length} shared job
                                    role
                                    {filteredManageJobRoles.length === 1
                                      ? ""
                                      : "s"}
                                  </span>

                                  {getAppliedManageJobFiltersCount() > 0 && (
                                    <>
                                      <span className="smart-filter-badge">
                                        {getAppliedManageJobFiltersCount()}
                                      </span>

                                      <span>filters applied</span>

                                      <button
                                        type="button"
                                        className="partner-directory-smart-clear"
                                        onClick={clearManageJobFilters}
                                      >
                                        Clear all
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {showManageJobFilters && (
                                <>
                                  <div className="smart-filter-divider" />

                                  <div className="smart-filter-content">
                                    <div className="smart-filter-grid">
                                      <div className="smart-filter-item">
                                        <label>Department</label>

                                        <select
                                          value={manageJobDepartmentFilter}
                                          onChange={(event) =>
                                            setManageJobDepartmentFilter(
                                              event.target.value,
                                            )
                                          }
                                          className={`smart-filter-select ${
                                            manageJobDepartmentFilter !== "All"
                                              ? "smart-active"
                                              : ""
                                          }`}
                                        >
                                          {jobRoleDepartmentOptions.map(
                                            (option) => (
                                              <option
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {option.label}
                                              </option>
                                            ),
                                          )}
                                        </select>
                                      </div>

                                      <div className="smart-filter-item">
                                        <label>Level</label>

                                        <select
                                          value={manageJobLevelFilter}
                                          onChange={(event) =>
                                            setManageJobLevelFilter(
                                              event.target.value,
                                            )
                                          }
                                          className={`smart-filter-select ${
                                            manageJobLevelFilter !== "All"
                                              ? "smart-active"
                                              : ""
                                          }`}
                                        >
                                          {roleLevels.map((option) => (
                                            <option
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="u-page-actions partner-directory-bulk-toolbar partner-manage-bulk-toolbar">
                            <label className="select-all-wrap partner-directory-select-all">
                              <input
                                type="checkbox"
                                checked={isAllVisibleJobsSelected}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate =
                                      someVisibleJobRolesSelected;
                                  }
                                }}
                                disabled={filteredManageJobRoles.length === 0}
                                onChange={(event) =>
                                  toggleAllManagedJobs(event.target.checked)
                                }
                                aria-label="Select all visible shared job roles"
                              />

                              <span>
                                Select All
                                {isAllVisibleJobsSelected &&
                                filteredManageJobRoles.length
                                  ? ` (${filteredManageJobRoles.length})`
                                  : ""}
                              </span>
                            </label>

                            <BulkAction
                              options={[
                                {
                                  value: "remove-shared-job-roles",
                                  label: "Remove",
                                  disabled:
                                    !partnerAccess.canBulkRemovePartners,
                                },
                              ]}
                              selectedIds={selectedManagedJobRoleIds}
                              onAction={(action) => {
                                if (action === "remove-shared-job-roles") {
                                  handleBulkRemoveManagedJobRoles();
                                }
                              }}
                              label="Bulk Action"
                              placeholder="Select bulk action"
                              requireSelection={true}
                            />
                          </div>

                          <div className="partner-share-table-wrap">
                            <div className="partner-share-table jobs">
                              <div className="partner-share-table-header">
                                <div>
                                  <input
                                    type="checkbox"
                                    checked={isAllVisibleJobsSelected}
                                    ref={(input) => {
                                      if (input) {
                                        input.indeterminate =
                                          someVisibleJobRolesSelected;
                                      }
                                    }}
                                    onChange={(event) =>
                                      toggleAllManagedJobs(event.target.checked)
                                    }
                                    aria-label="Select all visible job roles"
                                  />
                                </div>

                                <div>Job Role</div>
                                <div>Department</div>
                                <div>Level</div>
                              </div>

                              {filteredManageJobRoles.length > 0 ? (
                                filteredManageJobRoles.map((role) => {
                                  return (
                                    <div
                                      key={role.id}
                                      className="partner-share-table-row"
                                    >
                                      <div>
                                        <input
                                          type="checkbox"
                                          checked={selectedManagedJobRoleIds.includes(
                                            role.id,
                                          )}
                                          onChange={(event) =>
                                            toggleManagedJobSelection(
                                              role.id,
                                              event.target.checked,
                                            )
                                          }
                                          aria-label={`Select ${
                                            role.name ||
                                            role.roleName ||
                                            role.id
                                          }`}
                                        />
                                      </div>

                                      <div className="partner-share-name-cell">
                                        <strong>
                                          {role.name || role.roleName}
                                        </strong>

                                        <span>{role.jobId || role.id}</span>
                                      </div>

                                      <div>{role.department}</div>

                                      <div>{role.level}</div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="partner-share-empty">
                                  No job roles match the current filters.
                                </div>
                              )}
                            </div>
                          </div>
                        </section>
                      )}

                    {!managingPartner.shareProfiles &&
                      !managingPartner.shareJobRoles && (
                        <div className="partner-share-empty large">
                          Enable Profile Sharing or Job Role Sharing above to
                          begin selecting information for this partner.
                        </div>
                      )}
                  </div>
                )}
              </div>

              <footer className="mmh-org-modal-footer partner-manage-footer">
                {partnerActionMode === "review" && (
                  <>
                    {inviteDecision === "" && (
                      <>
                        <button
                          type="button"
                          className="mmh-btn partner-danger-outline-btn"
                          onClick={() => setInviteDecision("decline")}
                        >
                          Decline
                        </button>

                        <button
                          type="button"
                          className="mmh-btn mmh-btn-secondary"
                          onClick={handleReviewInviteLater}
                        >
                          Review Later
                        </button>

                        <button
                          disabled={!partnerAccess.canEditPartner}
                          type="button"
                          className="mmh-btn mmh-btn-primary"
                          onClick={() => setInviteDecision("accept")}
                        >
                          Accept
                        </button>
                      </>
                    )}

                    {inviteDecision === "accept" && (
                      <>
                        <button
                          type="button"
                          className="mmh-btn mmh-btn-secondary"
                          onClick={() => setInviteDecision("")}
                        >
                          Back
                        </button>

                        <button
                          disabled={!partnerAccess.canEditPartner}
                          type="button"
                          className="mmh-btn mmh-btn-primary"
                          onClick={() =>
                            acceptPartnerInvite(
                              managingPartner.id || managingPartner._id,
                              "ACCEPTED",
                            )
                          }
                        >
                          Accept Invitation
                        </button>
                      </>
                    )}

                    {inviteDecision === "decline" && (
                      <>
                        <button
                          type="button"
                          className="mmh-btn mmh-btn-secondary"
                          onClick={() => setInviteDecision("")}
                        >
                          Back
                        </button>

                        <button
                          type="button"
                          className="mmh-btn mmh-btn-danger"
                          onClick={() =>
                            acceptPartnerInvite(
                              managingPartner.id || managingPartner._id,
                              "REJECTED",
                            )
                          }
                        >
                          Decline Invitation
                        </button>
                      </>
                    )}
                  </>
                )}

                {partnerActionMode === "track" && (
                  <>
                    <button
                      type="button"
                      className="mmh-btn mmh-btn-secondary"
                      onClick={closePartnerActionModal}
                    >
                      Close
                    </button>

                    {!isExpiredPartnerInvitation(managingPartner) && (
                      <button
                        disabled={!partnerAccess.canEditPartner}
                        type="button"
                        className="mmh-btn partner-danger-outline-btn"
                        // onClick={() =>
                        //   handleCancelPartnerInvitation(managingPartner)
                        // }
                        onClick={() =>
                          handleCancelInvitation(managingPartner._id)
                        }
                      >
                        Cancel Invite
                      </button>
                    )}

                    {isSentPartnerInvitation(managingPartner) && (
                      <button
                        disabled={!isExpiredPartnerInvitation(managingPartner)}
                        type="button"
                        className="mmh-btn mmh-btn-primary"
                        onClick={() => resendPartnerInvitation(managingPartner)}
                      >
                        Resend Invite
                      </button>
                    )}
                  </>
                )}

                {partnerActionMode === "manage" && (
                  <>
                    <button
                      type="button"
                      className="mmh-btn mmh-btn-secondary"
                      onClick={closePartnerActionModal}
                    >
                      Cancel
                    </button>

                    <button
                      // disabled={!partnerAccess.canEditPartner}
                      type="button"
                      className="mmh-btn mmh-btn-primary"
                      onClick={handleSaveManagedSharing}
                    >
                      Save Changes
                    </button>
                  </>
                )}
              </footer>
            </>
          )}
        </div>
      </div>

      {showDisablePartnerCollaborationModal && (
        <div className="partner-bulk-delete-overlay">
          <div className="partner-bulk-delete-modal partner-warning-modal partner-disable-collaboration-modal">
            <div className="partner-bulk-delete-header">
              <div className="partner-warning-title">
                <span>
                  <Unlink size={19} />
                </span>

                <h2>Disable Partner Collaboration</h2>
              </div>

              <button
                type="button"
                onClick={() => setShowDisablePartnerCollaborationModal(false)}
                aria-label="Close disable confirmation"
              >
                <X size={20} />
              </button>
            </div>

            <div className="partner-bulk-delete-body">
              <p>Are you sure you want to disable Partner Collaboration?</p>

              <strong className="partner-warning-list-title">
                Disabling this feature will:
              </strong>

              <ul className="partner-warning-list">
                <li>Remove all active partner connections</li>
                <li>
                  Stop sharing profiles and job roles with partner organizations
                </li>
                <li>Remove access to shared profiles and job roles</li>
                <li>Hide Talent Solutions and Hiring Solutions</li>
              </ul>

              <div className="partner-warning-preserved-note">
                Your organization's profiles, job roles, and historical records
                will not be deleted.
              </div>

              <p className="partner-warning-final-note">
                This action can be reversed later, but new partner invitations
                must be sent to re-establish connections.
              </p>
            </div>

            <div className="partner-bulk-delete-footer">
              <button
                type="button"
                className="mmh-btn mmh-btn-secondary"
                onClick={() => setShowDisablePartnerCollaborationModal(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="mmh-btn mmh-btn-danger"
                disabled={isUpdatingPartnerCollaboration}
                onClick={handleDisablePartnerCollaboration}
              >
                {isUpdatingPartnerCollaboration ? "Disabling..." : "Disable"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={Boolean(capabilityWarningMode)}
        type="warning"
        title={
          capabilityWarningMode === "profiles"
            ? "Stop sharing profiles?"
            : "Stop sharing job roles?"
        }
        message={
          capabilityWarningMode === "profiles"
            ? "Turning this off will stop sharing all currently shared profiles with this partner."
            : "Turning this off will stop sharing all currently shared job roles with this partner."
        }
        showButton={true}
        confirmLabel={
          capabilityWarningMode === "profiles"
            ? "Stop Sharing Profiles"
            : "Stop Sharing Job Roles"
        }
        onClose={() => setCapabilityWarningMode("")}
        onConfirm={confirmDisableManagedCapability}
      >
        <div
          style={{
            textAlign: "left",
            marginTop: "16px",
            color: "var(--text-secondary)",
            lineHeight: 1.6,
          }}
        >
          <p style={{ margin: "0 0 12px" }}>
            {capabilityWarningMode === "profiles"
              ? "If you turn profile sharing back on later, previously shared profiles won't be restored automatically. You'll need to share them again."
              : "If you turn job role sharing back on later, previously shared job roles won't be restored automatically. You'll need to share them again."}
          </p>

          <p style={{ margin: 0 }}>
            {capabilityWarningMode === "profiles"
              ? "The profiles will remain available within your organization."
              : "The job roles will remain available within your organization."}
          </p>
        </div>
      </Modal>

      <Modal
        show={Boolean(stopSharingMode)}
        type="warning"
        title="Stop Sharing Selected Items?"
        message="You are about to stop sharing the selected profiles and/or job roles with this partner."
        showButton={true}
        confirmLabel="Stop Sharing"
        onClose={() => setStopSharingMode("")}
        onConfirm={confirmStopSharingSelectedItems}
      >
        <div
          style={{
            textAlign: "left",
            marginTop: "16px",
            color: "var(--text-secondary)",
            lineHeight: 1.6,
          }}
        >
          <p style={{ margin: "0 0 12px" }}>
            Once removed, the partner will no longer have access to these items,
            and they will no longer appear in their Hiring results.
          </p>

          <p style={{ margin: 0 }}>
            These items will remain available within your organization.
          </p>
        </div>
      </Modal>

      <Modal
        show={showPartnerBulkDeleteModal}
        type="warning"
        title="Remove Partner Connection?"
        message="You are about to remove the connection with the selected partner organization(s)."
        showButton={true}
        confirmLabel="Remove"
        onClose={() => {
          setShowPartnerBulkDeleteModal(false);
          setSinglePartnerIdToRemove(null);
        }}
        onConfirm={handleBulkRemoveConnections}
      >
        <div
          style={{
            textAlign: "left",
            marginTop: "16px",
            color: "var(--text-secondary)",
            lineHeight: 1.6,
          }}
        >
          <p style={{ marginBottom: "8px" }}>
            Removing this connection will:
          </p>

          <ul style={{ paddingLeft: "20px", margin: "0 0 16px" }}>
            <li>Stop profile and job role sharing between organizations</li>
            <li>Remove access to profiles and job roles shared by the partner</li>
            <li>Remove partner-shared data from Hiring</li>
            <li>End all active sharing permissions for this connection</li>
          </ul>

          <p style={{ margin: 0 }}>
            Your own profiles, job roles, shortlists, and history will not be deleted.
          </p>
        </div>
      </Modal>
      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            action: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={false}
      >
        {showModal.showButtons && (
          <div className="modal-actions">
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowModal({
                  show: false,
                  type: "",
                  title: "",
                  message: "",
                  action: null,
                })
              }
            >
              {t("common.cancel")}
            </button>
            <button className="btn btn-primary" onClick={showModal.action}>
              {t("organizationInfo.delete")} {/* Custom delete button text */}
            </button>
          </div>
        )}
      </Modal>

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default OrganizationInfoPage;
